<template>
  <div
    v-if="$register && $q.screen.lt.sm"
    class="column q-pa-sm items-center"
    data-test-id="payments-form"
  >
    <totals-section
      class="full-width"
      :order="order"
      :hide-amounts="hideAmounts"
      :show-title="false"
    />
    <order-contact-details
      v-if="areOrderContactDetailsRequired"
      ref="orderContactDetails"
      :order="order"
      @show-marketing-emails-opt-in="val => showMarketingEmailsOptIn = val"
      @receive-marketing-emails="val => receiveMarketingEmails = val"
    />
    <div
      v-show="order.total > 0 && paymentAllowed"
      class="q-mx-xs full-width"
    >
      <payment-list
        :split="split"
        :show-title="$register && !!payments.length"
        :show-payment-list="!!payments.length && paymentAllowed"
        :split-amount="splitAmount"
        :split-ratio="splitRatio"
      >
        <template #payment-line-items>
          <q-item
            v-if="!$register"
            class="bg-responsive-grey-3"
          >
            <q-item-section avatar>
              <q-avatar
                icon="fas fa-gift-card"
                text-color="responsive"
              />
            </q-item-section>

            <q-item-section :class="$q.screen.gt.xs ? 'text-h6' : 'text-weight-bolder text-body1'">
              {{ $t('payment.actions.payWithMethod', { method: $tc('giftCard.giftCard', 1) }) }}
            </q-item-section>
          </q-item>
          <payment-slide-items
            :key="paymentSlideItemsKey"
            :payments="payments"
            :disable="paymentConnectorPaymentInProgress"
            @remove-payment="payment => removePayment(payment)"
            @edit-payment="editPayment"
          />
        </template>
      </payment-list>
    </div>

    <split-payment-section
      v-if="showEditSplit"
      class="full-width"
      :model-value="showEditSplit"
      :split-amount="splitAmount"
      :order="order"
      @update:model-value="value => showEditSplit = value"
      @custom-split-amount="value => customSplitAmount = value"
      @split-ratio="value => splitRatio = value"
    />

    <div
      v-else-if="showOtherOptions && !showCashOptions"
      class="full-width"
    >
      <other-payment-section
        @show-cash-options="otherPaymentCashButton"
        @pay-with-payment-type="paymentType => {
          addPaymentType(paymentType)
          $emit('display-other-options', false)
        }"
      />
    </div>
    <div
      v-else-if="showCashOptions"
      class="full-width"
    >
      <cash-buttons-section
        class="row justify-evenly"
        :allow-cash-purchase="allowCashPurchase"
        :order="order"
        :payment-connector-payment-in-progress="paymentConnectorPaymentInProgress"
        :cash-session-loading="cashSessionLoading"
        :exact-cash-amount="exactCashAmount"
        :quick-cash-amount-options="quickCashAmountOptions"
        :split="split"
        @add-cash="amount => addCash(amount)"
        @edit-cash="editCashPayment"
      />
      <div
        v-if="(placingOrRecaptcha || !loading) && order.remainingOwed <= 0 && !paymentConnectorPaymentInProgress"
        class="q-mt-sm q-mx-xs"
      >
        <div class="row items-center">
          <q-btn
            color="primary"
            :text-color="buttonTextColor"
            :rounded="!$register"
            size="md"
            class="q-mt-lg q-py-xs full-width"
            :disable="!!order.errors"
            :loading="placingOrRecaptcha"
            @click="placeOrder()"
          >
            Place Order :
            <i18n-n
              class="text-weight-bolder q-ml-sm"
              tag="span"
              :value="order.total"
              format="currency"
              scope="global"
              data-test-id="payment-total"
            />
          </q-btn>
        </div>
      </div>
    </div>
    <div
      v-else
      class="full-width"
    >
      <div
        v-if="showSavedUserPaymentMethodOption"
      >
        <q-btn
          color="secondary"
          :text-color="split ? undefined : buttonTextColor"
          size="md"
          class="q-mt-md q-mb-sm q-py-sm col full-width"
          :outline="split"
          :disable="savedUserPaymentMethodOptionDisabled"
          @click="payWithDefaultPaymentMethod"
        >
          {{ savedUserPaymentMethodButtonText }}
        </q-btn>
      </div>
      <div
        v-else-if="order.preAuthPayment"
      >
        <q-btn
          color="secondary"
          size="md"
          class="q-mt-md q-mb-sm q-py-sm col full-width"
          :outline="split"
          :disable="!$_.isEmpty(order.errors) || !preAuthPaymentMethodAmount"
          @click="payWithPreAuthPaymentMethod"
        >
          {{ $t('payment.actions.payAmountWithMethod', { amount: $n(preAuthPaymentMethodAmount, 'currency'), method: `${order.preAuthPayment.cardType} ...${order.preAuthPayment.lastFour}` }) }}
        </q-btn>
      </div>
      <div
        v-else-if="order.savedPaymentMethodId && (!split || !existingSavedPaymentMethod)"
      >
        <q-btn
          color="secondary"
          size="md"
          class="q-mt-md q-mb-sm q-py-sm col full-width"
          :outline="split"
          :disable="!$_.isEmpty(order.errors) || !savedPaymentMethodAmount"
          @click="payWithSavedPaymentMethod"
        >
          {{ $t('payment.actions.payAmountWithMethod', { amount: $n(savedPaymentMethodAmount, 'currency'), method: `${order.savedPaymentMethodBrand} ...${order.savedPaymentMethodLast4}` }) }}
        </q-btn>
      </div>
      <q-btn
        v-if="!$payments.status.connected"
        :class="$q.dark.isActive || $isColorDark(currentSite.primaryColor) ? 'text-white' : 'text-dark'"
        class="q-py-sm bg-primary q-my-sm full-width"
        :loading="connectingToReader || $payments.status.reconnecting"
        @click="$emit('reader-reconnect')"
      >
        {{ $payments.status.reconnecting ? 'Reconnection in Progress' : 'Reconnect To Chip Reader' }}
      </q-btn>
      <q-btn
        v-else
        class="q-py-sm bg-primary q-my-sm full-width"
        label="Pay With Card"
        rounded
        :class="$q.dark.isActive || $isColorDark(currentSite.primaryColor) ? 'text-white' : 'text-dark'"
        :loading="stripeReaderPaymentRequested"
        @click="payWithStripeReader"
      />
      <div
        v-for="giftCard in orderGiftCardPaymentMethods"
        :key="giftCard.id"
        class="q-mt-md q-mb-none"
      >
        <gift-card-and-credit-button
          v-if="!giftCardExhausted(giftCard)"
          is-button
          color="accent"
          :outline="!autoPlaceGiftCard(giftCard)"
          size="md"
          class="q-pb-xs q-py-sm full-width"
          :disable="!$_.isEmpty(order.errors) || !giftCardAmount(giftCard)"
          @click-button="addGiftCardPayment(giftCard)"
        >
          <template #buttonLabel>
            <template v-if="autoPlaceGiftCard(giftCard)">
              {{ $t('payment.actions.payAmountWithMethod', { amount: $n(giftCardAmount(giftCard), 'currency'), method: $tc('giftCard.giftCard', 1) }) }}
            </template>
            <template v-else>
              {{ $t('payment.actions.useGiftCardOfValue', { value: $n(giftCardAmount(giftCard), 'currency') }) }}
            </template>
          </template>
          <template #buttonSubtitle>
            {{ $t('pricing.balanceIsOfValue', { value: $n(giftCard.balance - (existingGiftCardPayment(giftCard) && existingGiftCardPayment(giftCard).amount || 0), 'currency') }) }}
          </template>
        </gift-card-and-credit-button>
      </div>
      <div
        v-if="promptForGiftCard && !paymentConnectorPaymentInProgress"
        class="q-my-sm full-width"
      >
        <checkout-code-input
          label="Gift Card number"
          checkout
          autofocus
          @add-checkout-code="code => $emit('add-checkout-code', code)"
        />
      </div>
      <mobile-order-option-list
        :split-amount="splitAmount"
        :split-ratio="splitRatio"
        :split="split"
        :show-taxable="currentSite.showTaxablePaymentOptionInPos && $register && order.total > 0 && order.remainingOwed > 0"
        :tax-exempt="order.taxExempt"
        :show-email-receipt="!!order.deliveryEmailAddress || (order.user && !!order.user.email)"
        :email-receipt="order.emailReceipt"
        :show-print-receipt="$register"
        :print-receipt="order.printReceipt"
        :payment-connector-payment-in-progress="paymentConnectorPaymentInProgress"
        :prompt-for-gift-card="promptForGiftCard"
        :order-can-transfer-tab="order.canTransferTab"
        @tax-exempt="value => order.taxExempt = value"
        @email-receipt="value => order.emailReceipt = value"
        @print-receipt="value => order.printReceipt = value"
        @show-gift-card="value => promptForGiftCard = value"
        @show-other-payment="otherPaymentClick"
        @split="value => showEditSplit = value"
        @show-incoming-employee="() => $emit('show-incoming-employee')"
      />
    </div>
    <edit-cash-dialog
      ref="editCashDialog"
      @set-cash="setCash"
    />
    <action-dialog
      v-model="showCashSessionDialog"
      title="Cash Session Required"
      :cancel="false"
      ok-label="Open Session"
      @ok="openCashSession"
    />
  </div>
  <div
    v-else-if="$register"
    class="col column justify-center"
    :class="{ 'q-px-sm': $q.screen.lt.sm }"
    :style="{ 'max-width': showEditSplit ? '825px' : '525px' }"
    data-test-id="payments-form"
  >
    <q-banner
      v-if="cashError"
      class="bg-warning q-mb-sm"
    >
      <template #avatar>
        <q-icon
          name="warning"
          color="negative"
        />
      </template>
      Cash payment requires cash drawer
    </q-banner>

    <order-error-banner :order="order" />

    <div class="row">
      <split-payment-section
        class="col-auto"
        style="width: 325px;"
        :model-value="showEditSplit"
        :split-amount="splitAmount"
        :restrict-split-by-order-item="splitRequiresPaymentByOrderItem"
        :order="order"
        @update:model-value="value => showEditSplit = value"
        @custom-split-amount="value => customSplitAmount = value"
        @split-ratio="value => splitRatio = value"
      />
      <q-separator
        v-if="showEditSplit"
        class="q-ml-sm q-mr-md"
        vertical
      />
      <div
        class="col"
        :style="'max-width: 500px; min-width: 450px;'"
      >
        <totals-section
          :order="order"
          :hide-amounts="hideAmounts"
        />
        <!-- donation section -->
        <q-list
          v-if="currentSite.showDonationOnRegister"
          bordered
          separator
          class="q-mx-xs"
        >
          <donation-widget
            checkout
            flat
            :order="order"
            :title="currentSite.donationTitle"
            :description="currentSite.donationDescription"
            :is-mobile-view="$q.screen.xs"
            :override-donation-types="currentSite.consumerConfig.consumerDonationTypes"
            :default-amounts="currentSite.consumerConfig.consumerDefaultDonationAmounts"
            :default-percents="currentSite.consumerConfig.consumerDefaultDonationPercents"
            :preselected-value="currentSite.consumerConfig.donationPreselectedDefaultValue"
            :style="$q.screen.xs ? 'max-width: 100vw;' : 'max-width: 600px;'"
          />
        </q-list>

        <!-- tip section -->
        <q-list
          v-if="tipWidgetEnabled && currentSite.tippingOptions.enable_consumer_qr_tips"
          bordered
          separator
          class="q-mx-xs"
        >
          <tip-widget
            checkout
            flat
            :title="currentSite.tippingOptions.consumer_widget_title"
            :description="currentSite.tippingOptions.consumer_widget_description"
            :override-amount-types="currentSite.tippingOptions.consumer_tip_types"
            :default-amount="currentSite.tippingOptions.consumer_default_amount"
            :default-percent="currentSite.tippingOptions.consumer_default_percent"
            :preselected-value="currentSite.tippingOptions.preselected_default_value"
            :style="$q.screen.xs ? 'max-width: 100vw;' : 'max-width: 600px;'"
          />
        </q-list>

        <order-contact-details
          v-if="areOrderContactDetailsRequired"
          ref="orderContactDetails"
          :order="order"
          @show-marketing-emails-opt-in="val => showMarketingEmailsOptIn = val"
          @receive-marketing-emails="val => receiveMarketingEmails = val"
        />
        <q-input
          v-if="order.phoneOrder && currentSite.anonymousOrderRequirements.phone_prompt_for_comments"
          v-model="order.comments"
          class="q-mx-xs"
          label="Comments"
          bottom-slots
        />
        <div
          v-if="order.anonymous && showMarketingEmailsOptIn"
          class="q-mx-xs"
        >
          <q-field>
            <template #control>
              <q-checkbox
                v-model="receiveMarketingEmails"
              >
                {{ $t('assertion.happyToReceiveMarketingFrom', { siteName: currentSite.name }) }}.
              </q-checkbox>
            </template>
          </q-field>
        </div>

        <div
          v-if="needUserPhone || (user && !(user.firstName && user.lastName))"
          id="order-details"
          class="q-mx-xs q-mt-md"
        >
          <q-list
            bordered
            separator
          >
            <h2>
              {{ $t('pages.payment.updateDetailsPrompt') }}
            </h2>
            <div>
              <q-input
                v-if="user && !user.firstName"
                ref="firstName"
                v-model="user.firstName"
                class="col"
                :label="$t('form.labels.firstName')"
                :rules="[val => !!val || $t('form.errors.required')]"
                bottom-slots
                lazy-rules
              />
              <q-input
                v-if="user && !user.lastName"
                ref="lastName"
                v-model="user.lastName"
                class="col"
                :label="$t('form.labels.lastName')"
                :rules="[val => !!val || $t('form.errors.required')]"
                bottom-slots
                lazy-rules
              />
              <phone-number-input
                v-if="needUserPhone"
                :phone="user.phone"
                :override-country-code="user.phoneCountryCode"
                class="col"
                :required="currentSite.anonymousOrderRequirements.phone_phone_required"
                @phone-changed="handlePhoneChange"
              />
            </div>
          </q-list>
        </div>

        <!-- invoice section -->
        <div
          v-if="order.invoicedOrder"
          id="order-details"
          class="q-mx-xs q-mt-md"
        >
          <h2>Invoice User</h2>

          <div
            v-if="!order.user || !order.user.isInvoiceable"
            class="row justify-around"
          >
            <q-btn
              class="col-8 q-ma-xs"
              color="primary"
              :text-color="buttonTextColor"
              :label="$t('user.actions.find')"
              @click="findInvoiceUser()"
            />
            <q-btn
              class="col-auto q-ma-xs"
              color="secondary"
              label="Create User"
              @click="$emit('edit-user', null, true)"
            />
          </div>
          <div v-else>
            <q-chip
              v-if="user && !user.loading"
              class="col text-bold"
              style="margin-top: 10px; margin-bottom: 3px; height: 40px;"
              :color="user.membership && user.membership.state =='active' && !user.membershipType.free ? 'primary' : 'grey-7'"
              size="lg"
              text-color="white"
              :removable="order.state === 'draft'"
              clickable

              @remove="order.removeUser()"
            >
              <div style="display: inline-grid;">
                <span
                  class="ellipsis"
                >
                  {{ user.firstName }}
                  <span class="sensitive-data">{{ user.lastName }}</span>
                  {{ user.companyName ? '-' + user.companyName : '' }}
                </span>
              </div>
            </q-chip>
          </div>
        </div>

        <!-- payments list -->
        <div
          v-show="(order.total > 0 || paymentConnectorPaymentInProgress) && paymentAllowed && $register"
          class="q-mx-sm"
        >
          <payment-list
            :split="split"
            :show-title="!!payments.length"
            :show-payment-list="!!payments.length && paymentAllowed"
            :split-amount="splitAmount"
            :split-ratio="splitRatio"
          >
            <template #actions>
              <q-toggle
                v-if="$register"
                v-model="split"
                :disable="paymentConnectorPaymentInProgress"
                class="col-auto"
                @update:model-value="value => showEditSplit = value"
              >
                Split
              </q-toggle>
            </template>
            <template #payment-line-items>
              <payment-slide-items
                :key="paymentSlideItemsKey"
                :payments="payments"
                :disable="paymentConnectorPaymentInProgress"
                @remove-payment="payment => removePayment(payment)"
                @edit-payment="editPayment"
              />
            </template>
          </payment-list>
        </div>

        <!-- edit payments section -->
        <div
          v-if="payments.length && order.remainingOwed > 0 && paymentAllowed && $register"
          class="row justify-end q-gutter-sm q-mt-xs q-mb-md q-mx-lg"
        >
          <div class="col-auto">
            {{ $t('pricing.remaining') }}
          </div>
          <i18n-n
            class="col-auto"
            tag="span"
            :value="order.remainingOwed"
            format="currency"
            scope="global"
          />
        </div>

        <negative-payment-sliders
          :order="order"
          :credits="credits"
          @edit-payment="editPayment"
        />

        <div
          v-if="order.changeDue"
          class="q-mt-sm q-mx-xs"
          style="margin-top: -15px;"
        >
          <h2>{{ $t('pages.payment.changeDueHeading') }}</h2>

          <q-list
            bordered
            separator
          >
            <q-item class="row">
              <q-item-section class="col-grow">
                <q-item-label
                  overline
                  class="text-uppercase text-weight-bolder"
                >
                  {{ $t('pricing.change') }}
                </q-item-label>
              </q-item-section>
              <q-item-section class="text-right">
                <i18n-n
                  class="text-bold"
                  tag="span"
                  :value="order.changeDue"
                  format="currency"
                  scope="global"
                />
              </q-item-section>
            </q-item>
          </q-list>
        </div>

        <div
          class="overflow-hidden"
        >
          <transition
            leave-active-class="animated fadeOutUp"
            enter-active-class="animated fadeInDown"
          >
            <div
              v-show="order.remainingOwed > 0 && paymentAllowed"
              class="overflow-hidden q-mx-xs"
            >
              <div
                v-if="promptForGiftCard && !paymentConnectorPaymentInProgress"
                class="q-my-sm"
              >
                <checkout-code-input
                  :label="$t('giftCard.giftCardNumber')"
                  checkout
                  autofocus
                  @add-checkout-code="code => $emit('add-checkout-code', code)"
                />
              </div>
              <div
                v-for="giftCard in orderGiftCardPaymentMethods"
                :key="giftCard.id"
                class="q-mb-none q-mt-md"
              >
                <gift-card-and-credit-button
                  is-button
                  :value="useGiftCardBalance[giftCard.id]"
                  color="accent"
                  :outline="!autoPlaceGiftCard(giftCard)"
                  size="md"
                  :disable="!$_.isEmpty(order.errors) || !giftCardAmount(giftCard)"
                  @click-button="addGiftCardPayment(giftCard)"
                  @update:model-value="(value) => useGiftCardBalance[giftCard.id] = value"
                >
                  <template #buttonLabel>
                    <template v-if="autoPlaceGiftCard(giftCard)">
                      {{ $t('payment.actions.payAmountWithMethod', { amount: $n(giftCardAmount(giftCard), 'currency'), method: $tc('giftCard.giftCard', 1) }) }}
                    </template>
                    <template v-else>
                      {{ $t('payment.actions.useGiftCardOfValue', { value: $n(giftCardAmount(giftCard), 'currency') }) }}
                    </template>
                  </template>
                  <template #buttonSubtitle>
                    {{ $t('pricing.balanceIsOfValue', { value: $n(giftCard.balance - (existingGiftCardPayment(giftCard) && existingGiftCardPayment(giftCard).amount || 0), 'currency') }) }}
                  </template>
                </gift-card-and-credit-button>
              </div>
              <div
                v-if="showUseAccountGiftCardBalance"
                class="q-mt-md q-mb-none"
                data-test-id="payments-form-pay-with-gift-card-button"
              >
                <gift-card-and-credit-button
                  is-button
                  :value="useUserGiftCardBalance"
                  color="accent"
                  :text-color="$getColor(currentSite, 'accent')"
                  size="md"
                  @click-button="addGiftCardPayments()"
                  @update:model-value="(value) => useUserGiftCardBalance = value"
                >
                  <template #buttonLabel>
                    <template
                      v-if="user.currentCircuitGiftCardsBalance >= order.remainingOwed"
                    >
                      {{ $t('payment.actions.payAmountWithMethod', { amount: $n(order.remainingOwed, 'currency'), method: $tc('giftCard.giftCard', 2) }) }}
                    </template>
                    <template
                      v-else
                    >
                      {{ $t('payment.actions.useGiftCardOfValue', { value: $n(user.currentCircuitGiftCardsBalance, 'currency') }) }}
                    </template>
                  </template>
                  <template #buttonSubtitle>
                    <div
                      class="col text-subtitle2 text-center text-responsive"
                      style="border-radius: 0 0 4px 4px; border-bottom: 1px solid; border-right: 1px solid; border-left: 1px solid;"
                    >
                      {{ $t('pricing.balanceIsOfValue', { value: $n(user.currentCircuitGiftCardsBalance - (existingGiftCardPayment && existingGiftCardPayment.amount || 0), 'currency') }) }}
                    </div>
                  </template>
                </gift-card-and-credit-button>
              </div>
              <div
                v-if="showUseAccountCredit"
                class="q-mb-none q-mt-md"
                data-test-id="payments-form-pay-with-credit-button"
              >
                <gift-card-and-credit-button
                  is-button
                  :value="useAccountCredit"
                  color="positive"
                  :outline="user.credit < order.remainingOwed || split"
                  size="md"
                  :disable="!creditAmount"
                  @click-button="addAccountCredit()"
                  @update:model-value="(value) => useAccountCredit = value"
                >
                  <template #buttonLabel>
                    <template v-if="autoPlaceCredit">
                      {{ $t('payment.actions.payAmountWithMethod', { amount: $n(order.remainingOwed, 'currency'), method: $t('pricing.credit') }) }}
                    </template>
                    <template v-else>
                      {{ $t('payment.actions.useValueOfCredit', { value: $n(order.remainingOwed, 'currency') }) }}
                    </template>
                  </template>
                  <template #buttonSubtitle>
                    {{ $t('pricing.balanceIsOfValue', { value: $n(user.credit - (existingCreditPayment && existingCreditPayment.amount || 0), 'currency') }) }}
                  </template>
                </gift-card-and-credit-button>
              </div>
              <div
                v-if="showSavedUserPaymentMethodOption"
                class="row q-gutter-sm"
              >
                <q-btn
                  color="primary"
                  :text-color="split ? undefined : buttonTextColor"
                  size="md"
                  class="q-mt-md q-mb-sm q-py-sm col"
                  :outline="split"
                  :disable="savedUserPaymentMethodOptionDisabled"
                  data-test-id="payments-form-pay-with-default-card-button"
                  @click="payWithDefaultPaymentMethod"
                >
                  {{ savedUserPaymentMethodButtonText }}
                </q-btn>
                <q-btn
                  color="primary"
                  size="md"
                  class="q-mt-md q-mb-sm q-py-sm col-auto"
                  outline
                  icon="edit"
                  @click="$refs.setupIntentDialog.show(user)"
                />
              </div>

              <div
                v-if="(order.phoneOrder || order.invoicedOrder) && order.incompleteSwap"
                class="text-left q-pa-sm"
              >
                <q-banner class="bg-warning">
                  <template #avatar>
                    <q-icon
                      name="warning"
                      color="negative"
                    />
                  </template>
                  {{ $t('payment.actions.payLaterCannotBeUsedWithSwaps') }}
                  <br>
                  1. {{ $t('payment.actions.refundOriginalTickets') }}
                  <br>
                  2. {{ $t('payment.actions.startNewOrderForNewTickets') }}
                </q-banner>
              </div>
              <q-btn
                v-if="order.phoneOrder || order.invoicedOrder"
                class="q-py-sm full-width"
                label="Pay Later"
                color="green"
                size="md"
                :disable="order.incompleteSwap"
                @click="payLater"
              />
              <div
                v-if="order.savedPaymentMethodId && (!split || !existingSavedPaymentMethod)"
                class="row q-gutter-sm"
              >
                <q-btn
                  color="blue"
                  size="md"
                  class="q-mt-md q-mb-sm q-py-sm col"
                  :outline="split"
                  :disable="!$_.isEmpty(order.errors) || !savedPaymentMethodAmount"
                  @click="payWithSavedPaymentMethod"
                >
                  {{ $t('payment.actions.payAmountWithMethod', { amount: $n(savedPaymentMethodAmount, 'currency'), method: `${order.savedPaymentMethodBrand} ...${order.savedPaymentMethodLast4}` }) }}
                </q-btn>
              </div>
              <div
                v-else-if="order.preAuthPayment"
                class="row q-gutter-sm"
              >
                <q-btn
                  color="blue"
                  size="md"
                  class="q-mt-md q-mb-sm q-py-sm col full-width"
                  :outline="split"
                  :disable="!$_.isEmpty(order.errors) || !preAuthPaymentMethodAmount"
                  @click="payWithPreAuthPaymentMethod"
                >
                  {{ $t('payment.actions.payAmountWithMethod', { amount: $n(preAuthPaymentMethodAmount, 'currency'), method: `${order.preAuthPayment.cardType} ...${order.preAuthPayment.lastFour}` }) }}
                </q-btn>
              </div>
              <div
                v-else-if="showAddSavedCardButton"
                class="row q-gutter-sm"
              >
                <q-btn
                  color="grey"
                  size="md"
                  class="q-mt-md q-mb-sm q-py-xs col"
                  outline
                  @click="$emit('show-setup-intent-dialog', order)"
                >
                  Save Payment Method for Later
                </q-btn>
              </div>

              <div
                v-if="user && !user.isDayPassUser && !user.defaultPaymentMethodId"
                class="row q-gutter-sm"
              >
                <q-btn
                  color="primary"
                  text-color="primary"
                  size="md"
                  class="q-py-sm col q-my-md"
                  outline
                  icon="add"
                  icon-right="payment"
                  align="center"
                  data-test-id="payments-form-add-default-card-button"
                  @click="$refs.setupIntentDialog.show(user)"
                >
                  {{ $t('creditCard.actions.addDefault') }}
                </q-btn>
              </div>

              <div
                v-if="collectWithChipReader"
                class="q-mt-sm q-mb-xs"
              >
                <q-btn
                  v-if="!$payments.status.connected"
                  color="secondary"
                  size="md"
                  class="q-py-sm full-width"
                  :loading="connectingToReader || $payments.status.reconnecting"
                  outline
                  @click="$emit('reader-reconnect')"
                >
                  {{ $payments.status.reconnecting ? 'Reconnection in Progress' : 'Reconnect To Chip Reader' }}
                </q-btn>
                <q-btn
                  v-else-if="!currentSite.stripeReaderAutoPay && !stripeReaderPaymentRequested"
                  color="secondary"
                  size="md"
                  class="q-py-sm full-width"
                  outline
                  @click="payWithStripeReader"
                >
                  Pay&nbsp;
                  <i18n-n
                    :value="creditCardAmount"
                    format="currency"
                    scope="global"
                  />
                  &nbsp;With Chip Reader
                </q-btn>
                <q-btn
                  v-else-if="!order.phoneOrder || !order.draft"
                  color="secondary"
                  size="md"
                  class="q-py-sm q-py-xs full-width"
                  :disable="true"
                  :loading="$payments.status.processingPayment"
                  :outline="split"
                >
                  Pay&nbsp;
                  <i18n-n
                    :value="creditCardAmount"
                    format="currency"
                    scope="global"
                  />
                  &nbsp;With Chip Reader
                </q-btn>
                <div
                  class="col q-mt-xs text-subtitle2 text-grey text-center"
                >
                  <div v-if="!$payments.status.currentReader">
                    No chip reader selected
                  </div>
                  <div v-else-if="!$payments.status.connected">
                    Chip reader not connected
                  </div>
                  <div v-else-if="loadingTerminalIntent">
                    Configuring reader ...
                  </div>
                  <div v-else-if="loadingPaymentIntent">
                    Configuring payment ...
                  </div>
                  <div v-else-if="$payments.status.waitingForCustomerPayment">
                    Waiting for customer to provide payment
                  </div>
                  <div v-else-if="$payments.status.processingPayment">
                    Processing Payment ...
                  </div>
                  <div v-else-if="order.loading">
                    Saving order ...
                  </div>
                  <div
                    v-else-if="$payments.status.error && $payments.status.error.code !== 'canceled'"
                    class="text-negative"
                  >
                    {{ $payments.status.error }}
                  </div>
                  <div v-else-if="$payments.status.message">
                    {{ $payments.status.message }}
                  </div>
                  <div v-else-if="!$_.isEmpty(order.errors)">
                    {{ order.errors[0] }}
                  </div>
                </div>
              </div>

              <div
                v-show="externalPaymentConnectorMode && !paymentConnectorPaymentInProgress"
                class="q-mt-sm q-mb-xs"
              >
                <q-btn
                  v-if="paymentConnectorRequiredForCashPayments && cashPurchase"
                  :disable="!orderItemSplitAmountMatchesCashAmount"
                  color="primary"
                  size="md"
                  class="q-pb-xs q-mb-xs q-py-xs full-width"
                  @click="payWithPaymentConnector()"
                >
                  <div class="text-center row items-center align-center justify-center">
                    <div class="row text-center">
                      {{ $t('payment.actions.processCashPayments') }}
                    </div>
                    <div
                      v-if="!orderItemSplitAmountMatchesCashAmount"
                      class="row text-caption text-center"
                    >
                      {{ $t('payment.actions.cashAmountDoesNotMatchSplit') }}
                    </div>
                  </div>
                </q-btn>
                <q-btn
                  :disable="paymentConnectorRequiredForCashPayments && cashPurchase && !orderItemSplitAmountMatchesCashAmount"
                  :outline="!!split"
                  color="secondary"
                  data-test-id="payments-form-pay-with-payment-connector-button"
                  size="md"
                  class="q-pb-xs q-py-sm full-width"
                  @click="payWithPaymentConnector()"
                >
                  {{ $t('payment.actions.payAmountWithMethod', { amount: $n(paymentConnectorAmount, 'currency'), method: currentPaymentConnectorName }) }}
                </q-btn>
                <div v-if="paymentConnectorTransactionOptions && paymentConnectorTransactionOptions.length > 0">
                  <q-expansion-item
                    v-model="paymentConnectorTransactionOptionsExpanded"
                    popup
                    dense
                  >
                    <template #header>
                      <q-item-section avatar>
                        <q-avatar
                          icon="settings"
                          color="secondary"
                          text-color="white"
                        />
                      </q-item-section>

                      <q-item-section>
                        <q-item-label>
                          {{ $_.startCase(currentPaymentConnectorName) }} Transaction Options
                        </q-item-label>
                        <q-item-label caption>
                          {{ paymentConnectorTransactionOptionNames }}
                        </q-item-label>
                      </q-item-section>
                    </template>
                    <q-card>
                      <q-card-section class="row">
                        <div
                          v-for="option in paymentConnectorTransactionOptions"
                          :key="option.key"
                          class="col-6 items-center q-mt-xs"
                        >
                          <q-toggle
                            v-if="option.type === 'boolean'"
                            v-model="order.paymentConnectorTransactionOptions[option.key]"
                            :label="option.label"
                          />
                          <q-select
                            v-else-if="option.type === 'select'"
                            v-model="order.paymentConnectorTransactionOptions[option.key]"
                            :label="option.label"
                            :options="option.options"
                            map-options
                            use-chip
                            emit-value
                          />
                        </div>
                      </q-card-section>
                    </q-card>
                  </q-expansion-item>
                </div>
              </div>

              <div
                v-for="paymentTypeButton in paymentTypeButtons"
                v-show="paymentTypeButtons.length !== 0 && !paymentConnectorPaymentInProgress"
                :key="paymentTypeButton.id"
                class="q-mt-sm q-mb-xs"
              >
                <q-btn
                  :outline="!!split"
                  :style="`background: ${paymentTypeButton.color}; color: ${paymentTypeButton.color};`"
                  :text-color="getTextColor(paymentTypeButton.color)"
                  size="md"
                  class="q-pb-sm q-my-xs q-py-sm full-width"
                  @click="addPaymentType(paymentTypeButton)"
                >
                  Pay&nbsp;
                  <i18n-n
                    :value="existingPaymentTypePaymentAmounts[paymentTypeButton.id]"
                    format="currency"
                    scope="global"
                  />
                  &nbsp;By {{ paymentTypeButton.name }}
                </q-btn>
              </div>

              <div
                v-show="promptForManualCard && collectWithManualCard"
                class="q-mb-lg q-mt-md"
              >
                <q-list
                  class="bottom-square"
                  :class="{
                    'bg-responsive-grey-1': $q.dark.isActive && !stripeCardElementOnEditing && !stripeCardElementOnChange,
                    'bg-white': !$q.dark.isActive || stripeCardElementOnEditing ,
                    'bg-responsive-grey-9': $q.dark.isActive && stripeCardElementOnChange
                  }"
                  :style="{ border: stripeCardElementOnEditing ? '3px solid rgba(0, 122, 255, 0.5)' : undefined }"
                  bordered
                >
                  <q-item>
                    <q-item-section>
                      <div
                        id="manual-card-element"
                        class="q-py-xs"
                      />
                      <div v-if="payWithNewManualCardErrors">
                        <div
                          class="text-red q-mt-sm"
                        >
                          <q-icon
                            name="error"
                          />
                          {{ payWithNewManualCardErrors }}
                        </div>
                      </div>
                    </q-item-section>
                  </q-item>
                </q-list>
                <q-btn
                  color="primary"
                  :text-color="split ? undefined : buttonTextColor"
                  size="md"
                  padding="sm"
                  class="full-width square-top"
                  :disable="!$_.isEmpty(order.errors) || disablePayWithNewManualCard || loadingPaymentIntent || !creditCardAmount || placingOrRecaptcha || paymentConnectorPaymentInProgress"
                  :outline="split"
                  data-test-id="payments-form-pay-with-card-button"
                  @click="payWithNewManualCard"
                >
                  Pay&nbsp;
                  <i18n-n
                    :value="creditCardAmount"
                    format="currency"
                    scope="global"
                  />
                  &nbsp;With Card
                </q-btn>
              </div>

              <div
                v-show="allowCashPurchase && order.total && order.remainingOwed && !paymentConnectorPaymentInProgress"
                class="row q-my-sm justify-between q-gutter-xs"
              >
                <q-btn
                  color="green"
                  class="q-py-xs q-mx-xs q-mt-sm quick-cash-button"
                  :disable="cashSessionLoading || !$_.isEmpty(order.errors) || !exactCashAmount"
                  :outline="split"
                  dense
                  data-test-id="payments-form-exact-cash-amount-button"
                  @click="addCash(split ? exactCashAmount : 'exact', !split)"
                >
                  {{ split ? 'Add' : 'Pay' }}&nbsp;
                  <i18n-n
                    :value="exactCashAmount"
                    format="currency"
                    scope="global"
                  />
                  &nbsp;Cash
                  <q-inner-loading :showing="cashSessionLoading" />
                </q-btn>
                <q-btn
                  v-for="amount in quickCashAmountOptions"
                  :key="amount"
                  class="q-px-sm q-mt-sm q-py-xs quick-cash-button"
                  color="green"
                  outline
                  dense
                  :disable="cashSessionLoading"
                  @click="addCash(amount)"
                >
                  +
                  <i18n-n
                    :value="amount"
                    :format="Math.round(amount) === amount ? 'currencyNoCents' : 'currency'"
                    scope="global"
                  />
                  <q-inner-loading :showing="cashSessionLoading" />
                </q-btn>
                <q-btn
                  v-if="!splitRequiresPaymentByOrderItem"
                  color="green"
                  class="q-px-md quick-cash-button q-py-xs q-mt-sm"
                  outline
                  dense
                  label="Cash"
                  :disable="cashSessionLoading"
                  @click="addCash(0); editPayment(existingCashPayment)"
                >
                  <q-inner-loading :showing="cashSessionLoading" />
                </q-btn>
              </div>
            </div>
          </transition>
        </div>
        <div
          v-if="order.total > 0 && order.remainingOwed > 0 && paymentAllowed"
          class="q-my-sm row justify-evenly items-start"
        >
          <q-btn
            v-if="!promptForGiftCard && !paymentConnectorPaymentInProgress"
            color="accent"
            outline
            size="sm"
            class="col-auto"
            label="Add Gift Card"
            @click="promptForGiftCard = true"
          />
          <q-btn
            v-if="promptForGiftCard"
            color="accent"
            outline
            size="sm"
            class="col-auto"
            label="Hide Gift Card"
            @click="promptForGiftCard = false"
          />
          <q-btn
            v-if="collectWithManualCard && !paymentConnectorPaymentInProgress && !promptForManualCard"
            color="secondary"
            outline
            size="sm"
            class="col-auto"
            label="Manual Card"
            data-test-id="payments-form-manual-card-button"
            @click="promptForManualCard = true"
          />
          <q-btn
            v-if="!$payments.status.currentReader && !promptForManualCard && !externalPaymentConnectorMode"
            color="secondary"
            outline
            size="sm"
            class="col-auto"
            label="Select Chip Reader"
            @click="$emit('select-terminal')"
          />
          <q-btn
            v-if="promptForManualCard"
            color="secondary"
            outline
            size="sm"
            class="col-auto"
            label="Chip Reader"
            @click="promptForManualCard = false"
          />
          <q-btn
            v-if="registerType && registerType.showTipping"
            outline
            size="sm"
            class="col-auto"
            color="green"
            @click="$emit('show-add-tip-dialog')"
          >
            Tip
          </q-btn>
          <q-btn
            v-if="!paymentConnectorPaymentInProgress && (paymentTypeDropdownOptions.length !== 0 || showSavedUserPaymentMethodOption)"
            color="black"
            outline
            size="sm"
            content-class="text-center"
            label="Other"
            class="col-auto "
            @click="$refs.paymentTypeDialog.show()"
          />
        </div>
        <div
          v-if="externalPaymentConnectorMode && paymentConnectorActionOptions.length && !paymentConnectorActionSent"
        >
          <div
            class="row items-center"
          >
            <h2
              class="col q-mt-sm"
            >
              Reader Controls
            </h2>
            <div
              class="col-auto q-mt-sm"
            >
              <span v-if="existingPaymentConnectorPayment">
                {{ existingPaymentConnectorPayment.paymentConnectorResponseMessage }}
              </span>
            </div>
          </div>
          <div
            v-for="option in paymentConnectorActionOptions"
            :key="option.label"
            class="row justify-between"
          >
            <q-btn
              :label="option.label"
              :color="option.color"
              size="md"
              class="q-pb-xs q-py-sm full-width q-mb-sm"
              @click="performPaymentConnectorAction({ action: option.action, value: option.value })"
            />
          </div>
        </div>
        <div
          v-if="canForceCancelPaymentConnectorPayment"
          class="row justify-center align-center"
        >
          <q-btn
            size="sm"
            label="Force Cancel Transaction"
            color="negative"
            @click="forceCancelPaymentConnectorRequest"
          />
        </div>
        <div class="q-mt-sm row justify-evenly items-start">
          <q-toggle
            v-if="currentSite.showTaxablePaymentOptionInPos && order.total > 0 && order.remainingOwed > 0"
            v-model="order.taxExempt"
            :disable="paymentConnectorPaymentInProgress"
            class="col-auto"
            style="margin-top: -7px;"
            keep-color
            :color="order.taxExempt ? 'red' : 'green'"
            unchecked-icon="check"
            checked-icon="clear"
            :label="order.taxExempt ? 'Exempt' : 'Taxable'"
            @click.stop
          />
          <q-toggle
            v-if="order.deliveryEmailAddress || (order.user && order.user.email)"
            v-model="order.emailReceipt"
            class="col-auto"
            style="margin-top: -7px;"
            keep-color
            :color="order.emailReceipt ? 'green' : 'grey'"
            unchecked-icon="email"
            checked-icon="email"
            label="Email Receipt"
            @click.stop
          />
          <q-toggle
            v-model="order.printReceipt"
            class="col-auto"
            style="margin-top: -7px;"
            keep-color
            :color="order.printReceipt ? 'green' : 'grey'"
            unchecked-icon="receipt"
            checked-icon="receipt"
            :label="$t('payment.actions.printReceipt')"
            @click.stop
          />
          <q-toggle
            v-if="currentSite.anonymousOrderRequirements.allow_phone_orders"
            v-model="order.phoneOrder"
            class="col-auto"
            style="margin-top: -7px;"
            keep-color
            :disable="order.invoicedOrder"
            :color="order.phoneOrder ? 'green' : 'grey'"
            unchecked-icon="phone"
            checked-icon="phone"
            label="Phone Order"
            @click.stop
          />
          <q-toggle
            v-if="currentSite.allowInvoiceOrders && registerType && registerType.allowInvoiceOrders"
            v-model="order.invoicedOrder"
            class="col-auto"
            style="margin-top: -7px;"
            keep-color
            :disable="order.phoneOrder"
            :color="order.invoicedOrder ? 'green' : 'grey'"
            unchecked-icon="local_post_office"
            checked-icon="local_post_office"
            label="Invoice Order"
            @click.stop
          />
        </div>
        <div
          v-if="order.state === 'pending'"
        >
          <q-btn
            color="negative"
            size="md"
            outline
            class="q-mt-lg q-py-xs full-width"
            label="Cancel Order"
            @click="cancelOrder()"
          />
        </div>
        <div
          v-if="!loading && order.remainingOwed <= 0 && !paymentConnectorPaymentInProgress"
          class="q-mt-sm q-mx-xs"
        >
          <div class="row items-center">
            <q-btn
              color="primary"
              :text-color="buttonTextColor"
              size="md"
              class="q-mt-lg q-py-xs full-width"
              :disable="!$_.isEmpty(order.errors) || cashError || placingOrRecaptcha"
              :loading="placingOrRecaptcha"
              @click="placeOrder()"
            >
              Place Order :
              <i18n-n
                class="text-weight-bolder q-ml-sm"
                tag="span"
                :value="order.total"
                format="currency"
                scope="global"
                data-test-id="payment-total"
              />
            </q-btn>
          </div>
        </div>
        <q-inner-loading :showing="order.loading || loading" />
        <setup-intent-dialog
          ref="setupIntentDialog"
          @close="configureStripe"
          @reader-reconnect="$emit('reader-reconnect')"
        />
        <qr-code-ordering-disabled-dialog ref="QrCodeOrderingDisabledDialog" />
        <edit-cash-dialog
          ref="editCashDialog"
          @set-cash="setCash"
        />
        <action-dialog
          v-model="showCashSessionDialog"
          title="Cash Session Required"
          :cancel="false"
          ok-label="Open Session"
          @ok="openCashSession"
        />

        <user-table
          v-if="showInvoiceUserTable"
          ref="invoiceUserTable"
          :dialog="true"
          :show-all-filter="false"
          :show-users-filter="true"
          :show-employees-filter="false"
          :show-invoiceable-filter="true"
          :show-member-columns="false"
          :show-invoice-columns="true"
          :show-user-form="false"
          @select-user="selectUser"
        />

        <action-dialog
          ref="payLaterDialog"
          title="Draft Payments Exist"
          message="Do you want to process draft payments now?"
          ok-label="Process Now"
          cancel-label="Process Later"
          @ok="() => placeOrder(true, order, true)"
          @cancel="() => placeOrder(true)"
        />

        <payment-type-dialog
          ref="paymentTypeDialog"
          :payment-types="paymentTypeDropdownOptions"
          :payment-type-amounts="existingPaymentTypePaymentAmounts"
          :split="split"
          :show-saved-payment-method="showSavedUserPaymentMethodOption"
          :saved-payment-method-text="savedUserPaymentMethodButtonText"
          :disable-saved-payment-method="savedUserPaymentMethodOptionDisabled"
          @payment-type-selected="(paymentType) => addPaymentType(paymentType)"
        />
      </div>
    </div>
  </div>
  <div v-else>
    <div
      v-if="!areOrderContactDetailsRequired || areOrderContactDetailsValidated"
      class="items-center space-between"
    >
      <div
        class="column justify-around"
        style="height: 100%; min-height: 400px"
      >
        <div class="row justify-center text-center text-h2 text-bold">
          <div v-if="!externalPaymentConnectorMode && !$payments.status.connected">
            <div>
              {{ $t('pages.payment.errors.cardPaymentsUnavailable') }}
            </div>
            <div
              class="text-subtitle1"
              @click="kioskHiddenDialogClick"
            >
              <q-icon
                name="warning"
                color="negative"
              />
              {{ $t('pages.payment.errors.unableToCommunicateWithChipReaderPayAtCounter') }}
            </div>
          </div>
          <div v-else-if="loadingPaymentIntent || paymentConnectorRequestLoading">
            {{ $t('pages.payment.connectingToCardReader') }}
          </div>
          <div v-else-if="placingOrRecaptcha && !externalPaymentConnectorMode">
            <div>
              {{ $t('pages.payment.pleaseWaitWhileWePlaceYourOrder') }}
            </div>
          </div>
          <div v-else-if="!externalPaymentConnectorMode && !order.allowNonRecurringPayment">
            <div class="row">
              {{ $t('pages.payment.membershipRequiresRecurringPayments') }}
            </div>
            <div class="row justify-center">
              <q-btn
                v-if="user.defaultPaymentMethodId"
                color="primary"
                :text-color="buttonTextColor"
                class="q-mt-lg"
                size="lg"
                :label="$t('payment.actions.payAmountWithMethod', { method: $t('creditCard.savedCreditCard') })"
                @click="payWithDefaultPaymentMethod"
              />
              <q-btn
                v-else
                :label="$t('creditCard.actions.addDefault')"
                color="primary"
                :text-color="buttonTextColor"
                class="q-mt-lg"
                size="lg"
                @click="() => $refs.setupIntentDialog.show(user)"
              />
            </div>
          </div>
          <div v-else-if="collectWithChipReader || paymentConnectorPaymentInProgress">
            <div>
              {{ $t('pages.payment.insertSwipeOrTapCardPrompt') }}
            </div>
          </div>
          <div v-else>
            <div>
              {{ $t('pages.payment.errors.problemWithOrderTryAgain') }}
            </div>
          </div>
        </div>
        <div
          v-if="(externalPaymentConnectorMode || $payments.status.connected) && order.remainingOwned <= 0"
          class="row justify-center text-h3"
        >
          <div>
            {{ $t('common.or') }}
          </div>
        </div>
        <div
          v-if="(!externalPaymentConnectorMode && !$payments.status.connected) || order.remainingOwned <= 0"
          class="row justify-center"
        >
          <q-btn
            no-caps
            outline
            padding="xl"
            size="xl"
            :label="$t('pages.payment.actions.printAndPayAtCounter')"
            @click="payAtCounter"
          />
        </div>
      </div>
    </div>
    <action-dialog
      ref="orderContactDetailsDialog"
      :ok-label="$t('actions.confirm')"
      :cancel-label="$t('nav.actions.goBack')"
      :ok-dismiss="false"
      :cancel-dismiss="false"
      persistent
      @ok="validateOrderContactDetails"
      @cancel="() => $emit('back')"
    >
      <template #custom-message>
        <order-contact-details
          ref="orderContactDetails"
          :order="order"
          @show-marketing-emails-opt-in="val => showMarketingEmailsOptIn = val"
          @receive-marketing-emails="val => receiveMarketingEmails = val"
        />
      </template>
    </action-dialog>
    <action-dialog
      ref="kioskPaymentErrorDialog"
      :title="$t('pages.payment.errors.issueWithPayment')"
      :message="kioskPaymentErrorMessage"
      :ok-label="$t('actions.retry')"
      :cancel-label="$t('pages.payment.actions.payAtCounter')"
      persistent
      :ok-disable="paymentConnectorPaymentInProgress"
      @ok="() => externalPaymentConnectorMode ? payWithPaymentConnector() : configureStripeTerminal()"
      @cancel="payAtCounter"
    />
    <setup-intent-dialog
      ref="setupIntentDialog"
      @close="configureStripe"
      @reader-reconnect="$emit('reader-reconnect')"
      @new-payment-method-added="() => payWithDefaultPaymentMethod()"
    />
  </div>
</template>

<script>

import { required } from '@vuelidate/validators'
import { date, Dialog, Notify } from 'quasar'

const { formatDate } = date

import { isElectronMode, region } from 'src/config'
import CeaCard from 'src/models/cea-card/CeaCard'
import GiftCard from 'src/models/gift-card/GiftCard'
import Order, { ORDER_ACTIONS, ORDER_CHECKOUT_DEFAULT_OPTIONS, ORDER_PLACE_TYPES } from 'src/models/order/Order'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import { PAYMENT_STATES, PAYMENT_SUBTYPES, PAYMENT_TYPES } from 'src/models/payment/Payment'
import { POS_PAYMENT_TYPES } from 'src/models/register/RegisterStation'
import RegisterType from 'src/models/register/RegisterType'
import User from 'src/models/user/User'
import Voucher from 'src/models/voucher/Voucher'
import SetupIntentDialog from 'src/modules/admin/user/SetupIntentDialog'
import UserTable from 'src/modules/admin/user/UserTable'
import DonationWidget from 'src/modules/checkout/components/DonationWidget'
import TipWidget from 'src/modules/checkout/components/TipWidget'
import ActionDialog from 'src/modules/consumer/components/ActionDialog'
import PhoneNumberInput from 'src/modules/consumer/components/phone/PhoneNumberInput'
import CheckoutCodeInput from 'src/modules/register/components/cart/CheckoutCodeInput'
import CashButtonsSection from 'src/modules/register/components/CashButtonsSection'
import GiftCardAndCreditButton from 'src/modules/register/components/GiftCardAndCreditButton'
import OtherPaymentSection from 'src/modules/register/components/OtherPaymentSection'
import EditCashDialog from 'src/modules/register/payment/components/EditCashDialog'
import MobileOrderOptionList from 'src/modules/register/payment/components/MobileOrderOptionList'
import NegativePaymentSliders from 'src/modules/register/payment/components/NegativePaymentSliders'
import OrderContactDetails from 'src/modules/register/payment/components/OrderContactDetails.vue'
import OrderErrorBanner from 'src/modules/register/payment/components/OrderErrorBanner'
import PaymentList from 'src/modules/register/payment/components/PaymentList'
import PaymentSlideItems from 'src/modules/register/payment/components/PaymentSlideItems'
import PaymentTypeDialog from 'src/modules/register/payment/components/PaymentTypeDialog'
import SplitPaymentSection from 'src/modules/register/payment/components/SplitPaymentSection'
import TotalsSection from 'src/modules/register/payment/components/TotalsSection'
import PaymentMixin from 'src/modules/register/payment/mixins/PaymentMixin'
export const PAYMENT_CONNECTOR_PAYMENT_STARTED_AT_KEY = 'paymentConnectorStartedTimestamp'

export const PAYMENT_CONNECTOR_IN_PROGRESS_KEY = 'paymentConnectorInProgressOrder'

export default {
  name: 'PaymentsForm',
  components: {
    SetupIntentDialog,
    DonationWidget,
    ActionDialog,
    PaymentTypeDialog,
    SplitPaymentSection,
    UserTable,
    TipWidget,
    CheckoutCodeInput,
    TotalsSection,
    OtherPaymentSection,
    CashButtonsSection,
    PaymentSlideItems,
    PaymentList,
    MobileOrderOptionList,
    OrderErrorBanner,
    NegativePaymentSliders,
    EditCashDialog,
    OrderContactDetails,
    GiftCardAndCreditButton,
    PhoneNumberInput
  },
  mixins: [PaymentMixin],
  inject: ['checkoutData', 'recaptchaInProgress'],
  props: {
    order: {
      type: Object,
      required: true
    },
    hardware: {
      type: Object,
      required: false,
      default: null
    },
    registerType: {
      type: RegisterType,
      required: false,
      default: null
    },
    showTipping: {
      type: Boolean,
      required: false,
      default: false
    },
    placeType: {
      type: String,
      required: false,
      default: null
    },
    restrictCash: {
      type: Boolean,
      required: false,
      default: false
    },
    missingCashSession: {
      type: Boolean,
      required: false,
      default: false
    },
    unownedCashSession: {
      type: Boolean,
      required: false,
      default: false
    },
    paymentConnectorPaymentInProgress: {
      type: Boolean,
      required: false,
      default: false
    },
    loadingPaymentIntent: {
      type: Boolean,
      required: false,
      default: false
    },
    tipWidgetEnabled: {
      type: Boolean,
      required: false,
      default: false
    },
    hideAmounts: {
      type: Boolean,
      required: false,
      default: false
    },
    showCashOptions: {
      type: Boolean,
      required: false,
      default: false
    },
    showOtherOptions: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['display-other-options', 'reader-reconnect', 'add-checkout-code', 'show-incoming-employee', 'edit-user', 'show-setup-intent-dialog', 'select-terminal', 'show-add-tip-dialog', 'display-cash-options', 'order-placed', 'order-canceled', 'request-authorization', 'loading-payment-intent', 'timeout', 'open-cash-session', 'back'],
  data () {
    return {
      loading: false,
      cashSessionLoading: false,

      formatDate,

      stripeAPIToken: 'pk_test_w1G4K56RCVdwwZ1T1FdSsQC7',
      stripe: '',

      elements: '',
      card: '',
      intentToken: '',

      terminalIntentToken: null,
      loadingTerminalIntent: false,

      // Default for register is to use the chip reader
      promptForManualCard: false,
      disablePayWithNewManualCard: true,
      payWithNewManualCardErrors: '',

      oneButtonPayAvailable: false,

      promptForGiftCard: false,

      useGiftCardBalance: {},
      useAccountCredit: false,
      useUserGiftCardBalance: false,

      orderLoading: false,

      connectingToReader: false,

      customSplitAmount: null,
      splitRatio: null,
      showEditSplit: false,

      giftCardCode: '',
      giftCardLoading: false,
      giftCardErrorMessage: '',

      promoCode: '',
      promoCodeLoading: false,
      promoCodeErrorMessage: '',
      promoCodeErrorRestrictions: '',

      showCashSessionDialog: false,
      cashReceived: 0,

      placing: false,

      paymentConnectorActionSent: false,

      stripeReaderPaymentRequested: false,

      selectedPaymentType: null,
      showMarketingEmailsOptIn: false,
      receiveMarketingEmails: false,
      kioskHiddenDialogClickCount: 0,

      kioskStripeTimeoutInterval: null,
      kioskPaymentErrorMessage: null,

      paymentConnectorRequestLoading: false,

      paymentConnectorStatusInterval: null,

      showInvoiceUserTable: false,

      stripeCardElementOnEditing: false,
      stripeCardElementOnChange: false,

      paymentConnectorTransactionOptionsExpanded: false,
      paymentSlideItemsKey: 0,
      areOrderContactDetailsValidated: false
    }
  },
  computed: {
    cashPurchase () {
      return _.some(this.order.payments.data, { type: PAYMENT_TYPES.CASH, state: PAYMENT_STATES.DRAFT })
    },
    hardwareError () {
      if (this.electronMode) return false
      return !this.hardware ||
        !this.hardware.status.connected ||
        !this.hardware.status.receiptPrinterConnected ||
        !this.hardware.config.hasReceiptPrinter ||
        !this.hardware.config.hasCashDrawer
    },
    cashError () {
      return this.$register && this.cashPurchase && (!this.allowCashPurchase || this.hardwareError)
    },
    remainingLabel () {
      return (this.order.remainingOwed < 0) ? 'Change' : 'Remaining'
    },
    paymentAllowed () {
      if (this.$register && this.order.invoicedOrder && (!this.order.user || !this.order.user.isInvoiceable)) return false

      return true
    },
    showAddSavedCardButton () {
      if (!this.registerType?.showAddSavedCardButton) return false

      return this.$register && !this.user && !this.order.savedPaymentMethodId && !this.existingSavedPaymentMethod
    },
    splitAmount () {
      if (this.customSplitAmount) return this.customSplitAmount

      const splitRatio = this.splitRequiresPaymentByOrderItem ? 1 : this.splitRatio

      const splitAmount = this.computeSplitAmount(splitRatio)

      // Round up or down one cent to match total
      if (this.customSplitAmount === 0) {
        return this.order.remainingOwed
      } else if (this.order.remainingOwed < splitAmount) {
        return this.order.remainingOwed
      } else if (Math.abs(splitAmount * 100 - this.order.remainingOwed * 100) < 15) {
        return this.order.remainingOwed
      } else {
        return splitAmount
      }
    },
    orderItemSplitAmountMatchesCashAmount () {
      const payAmount = _.sumBy(this.order.orderItems.data, 'payAmount')
      const draftCashAmount = _.sumBy(_.filter(this.order.payments.data, { type: PAYMENT_TYPES.CASH, state: PAYMENT_STATES.DRAFT }), 'amount')

      return payAmount > 0 && payAmount === draftCashAmount
    },
    paymentConnectorRequiredForCashPayments () {
      return this.registerStation?.paymentConnectorRequiredForCash
    },
    ticketCount () {
      return _.filter(this.order.orderItems.data, { type: 'ticket' }).length
    },
    allowCashPurchase () {
      return this.currentSite.allowCashPayments &&
             (!this.$register || this.currentRegisterClient?.registerStation?.registerType?.allowCashPayments) &&
             (this.electronMode || (this.hardware && this.hardware.config.hasCashDrawer && this.order.allowNonRecurringPayment)) &&
             (!this.order.phoneOrder || !this.order.draft)
    },
    collectWithChipReader () {
      return (this.$station) && !this.externalPaymentConnectorMode && this.order.allowNonRecurringPayment && !this.promptForManualCard && this.$payments.status.currentReader
    },
    collectWithManualCard () {
      return this.order.allowNonRecurringPayment
    },
    electronMode () {
      return isElectronMode
    },
    movie () { return this.showing.movie },
    canProceed () { return true },
    showingId () {
      return _.find(this.order.orderItems.data, ticket => { return ticket.type === 'ticket' }).showing?.id
    },
    creditAmount () {
      return this.paymentAmount(this.existingCreditPayment?.amount, this.user?.credit)
    },
    autoPlaceCredit () {
      return this.autoPlace(this.existingCreditPayment?.amount, this.user?.credit)
    },
    paymentConnectorAmount () {
      return this.paymentAmount(this.existingPaymentConnectorPayment?.amount)
    },
    paymentConnectorActionOptions () {
      if (!this.existingPaymentConnectorPayment) return []

      return this.existingPaymentConnectorPayment.paymentConnectorStatusDetails?.all_actions || []
    },
    defaultPaymentMethodAmount () {
      return this.paymentAmount(this.existingDefaultPaymentMethod?.amount)
    },
    preAuthPaymentMethodAmount () {
      return this.paymentAmount(this.order?.preAuthPayment?.amount)
    },
    savedPaymentMethodAmount () {
      return this.paymentAmount(this.existingSavedPaymentMethod?.amount)
    },
    showSavedUserPaymentMethodOption () {
      return !!this.user &&
        !!this.user.defaultPaymentMethodId &&
        (!this.$register || (this.currentSite.showSavedCardPrimaryPaymentMethod || !this.order.allowNonRecurringPayment)) &&
        (!this.$consumer || this.currentSite.consumerConfig.showSavedCard) &&
        (!this.split || !this.existingDefaultPaymentMethod)
    },
    savedUserPaymentMethodOptionDisabled () {
      return this.showSavedUserPaymentMethodOption && (!_.isEmpty(this.order.errors) || !this.defaultPaymentMethodAmount)
    },
    savedUserPaymentMethodButtonText () {
      if (!this.showSavedUserPaymentMethodOption) return ''
      const method = `${this.user.defaultPaymentMethodBrand} ...${this.user.defaultPaymentMethodLast4}`
      return this.$i18n.t('payment.actions.payAmountWithMethod', { amount: this.$i18n.n(this.defaultPaymentMethodAmount, 'currency'), method })
    },
    isCashAllowed () {
      return !(this.restrictCash && (this.missingCashSession || this.unownedCashSession))
    },
    exactCashAmount () {
      return this.paymentAmount(this.existingCashPayment?.amount)
    },
    existingCreditPayment () {
      return _.find(this.order.payments.data, { type: PAYMENT_TYPES.CREDIT, state: PAYMENT_STATES.DRAFT })
    },
    existingCashPayment () {
      return _.find(this.order.payments.data, { type: PAYMENT_TYPES.CASH, state: PAYMENT_STATES.DRAFT })
    },
    existingNewManualCard () {
      // TODO, split out manual and terminal
      return _.find(this.order.payments.data, { type: PAYMENT_TYPES.CARD, subtype: PAYMENT_SUBTYPES.MANUAL, state: PAYMENT_STATES.DRAFT })
    },
    paymentConnectorOtherTransactionTypes () {
      // Some connectors have different transaction types, e.g. Foodstamp Cards, etc.
      return this.registerStation?.paymentConnectorOtherTransactionTypes || []
    },
    paymentConnectorTransactionOptions () {
      // Some connectors accept different transaction options, e.g. Force, Tip, etc.
      return this.registerStation?.paymentConnectorTransactionOptions || []
    },
    paymentConnectorTransactionOptionNames () {
      return _.map(this.paymentConnectorTransactionOptions, 'label').join(', ')
    },
    existingPreAuthPayment () {
      return this.order?.preAuthPayment
    },
    existingPaymentConnectorPayment () {
      const currentConnector = this.currentRegisterClient?.registerStation?.paymentConnectorName

      if (!currentConnector) return null

      return _.find(this.order.payments.data, { type: PAYMENT_TYPES.CARD, subtype: currentConnector, state: PAYMENT_STATES.DRAFT })
    },
    hasAvailableOnlinePromotions () {
      return _.some(this.discounts.data, discount => discount.availableOnline) || !!this.ceaCardVoucherType || !!this.meerkatMovieVoucherType
    },
    ceaCardVoucherType () {
      return this.currentSite.ceaCardEnabled ? _.find(this.voucherTypes.data, { isCeaCard: true }) : null
    },
    meerkatMovieVoucherType () {
      return this.currentSite.meerkatMovieEnabledValue ? _.find(this.voucherTypes.data, 'isMeerkatMovieValue') : null
    },
    promoCodeFieldLabel () {
      return this.ceaCardVoucherType ? 'Promo Code or CEA Card' : 'Promo Code'
    },
    quickCashAmountOptions () {
      return this.currentRegisterClient?.registerStation?.registerType?.quickCashAmountOptions || [20, 10, 5, 1]
    },
    enabledPaymentTypes () {
      return _.filter(this.paymentTypes.data, paymentType => paymentType.enabled)
    },
    paymentTypeButtons () {
      return _.sortBy(_.filter(this.enabledPaymentTypes, paymentType => paymentType.displayAsButton), 'displayOrder')
    },
    orderGiftCardPaymentMethods () {
      return _.filter(this.order?.giftCards?.data, giftCard => giftCard.balance > 0)
    },
    paymentTypeDropdownOptions () {
      return _.sortBy(_.filter(this.enabledPaymentTypes, paymentType => !paymentType.displayAsButton), 'displayOrder')
    },
    splitRequiresPaymentByOrderItem () {
      return this.registerStation?.paymentConnectorRequirePaymentByOrderItem
    },
    existingPaymentTypePayments () {
      const paymentTypes = {}
      _.each(this.paymentTypes.data, paymentType => {
        const matchingPayment = _.find(this.order.payments.data, { paymentType, state: PAYMENT_STATES.DRAFT })
        paymentTypes[paymentType.id] = matchingPayment
      })
      return paymentTypes
    },
    existingPaymentTypePaymentAmounts () {
      const existingPayments = this.existingPaymentTypePayments
      const paymentTypes = {}
      _.each(Object.keys(existingPayments), key => {
        paymentTypes[key] = this.paymentAmount(existingPayments[key]?.amount)
      })
      return paymentTypes
    },
    externalPaymentConnectorMode () {
      const currentConnectorName = this.currentPaymentConnectorName

      return currentConnectorName && currentConnectorName !== POS_PAYMENT_TYPES.STRIPE
    },
    currentPaymentConnectorPayment () {
      if (this.externalPaymentConnectorMode) {
        return _.find(this.order.payments.data, payment => payment.state === PAYMENT_STATES.DRAFT && (payment.subtype === this.currentPaymentConnectorName || (payment.type === PAYMENT_TYPES.CASH && this.registerStation?.paymentConnectorRequiredForCash)))
      } else {
        return null
      }
    },
    canForceCancelPaymentConnectorPayment () {
      const timeoutMs = 60 * 1000
      if (!this.currentPaymentConnectorPayment) return

      const paymentStartedAt = this.currentPaymentConnectorPayment.createdAt

      return paymentStartedAt && paymentStartedAt < this.currentSite.siteTime - timeoutMs
    },
    registerStation () {
      return this.currentRegisterClient?.registerStation
    },
    currentPaymentConnectorName () {
      return this.registerStation?.paymentConnectorName
    },
    needUserPhone () {
      return (
        this.order.phoneOrder &&
        this.currentSite.anonymousOrderRequirements.phone_prompt_for_phone &&
        this.user && !(this.user.original && this.user.original.phone)
      )
    },

    orderHasUnusedGiftCard () {
      return _.some(this.user.validGiftCardsForCurrentCircuit, giftCard => !this.existingGiftCardPayment(giftCard))
    },
    areOrderContactDetailsRequired () {
      if (this.$kiosk) {
        const isTicketPrintingDisabled = !!this.registerStation?.registerType?.kioskOptions?.disable_ticket_printing
        return this.$kiosk && isTicketPrintingDisabled && this.order.anonymous
      } else {
        return (!!this.order.deliveryType && this.order.deliveryType !== 'direct-to-customer' && this.order.deliveryType !== 'virtual') ||
          this.order.anonymous
      }
    },
    showUseAccountGiftCardBalance () {
      return this.user && this.user.currentCircuitGiftCardsBalance > 0 && this.orderHasUnusedGiftCard
    },
    showUseAccountCredit () {
      return this.user && this.user.credit && (!this.existingCreditPayment || this.existingCreditPayment.amount < this.user.credit)
    }
  },
  watch: {
    'order.phoneOrder' () {
      if (this.order.phoneOrder) {
        this.$payments.cancelExistingTerminalCollectionAttempt()
      } else {
        this.configureStripe()
      }
    },
    split () {
      if (!this.split) return

      if (this.customSplitAmount) return

      this.splitRatio ||= _.max([this.ticketCount, this.order.numberOfGuests || 0, 2])
    },
    async creditCardAmount () {
      // Handle updating the amount to be collected if a partial payment is added, e.g. credit or gift card
      await this.configureStripe()

      if (this.oneButtonPayAvailable) {
        await this.updateOnePaymentButton()
      }
    },
    paymentConnectorPaymentInProgress: {
      handler: function () {
        if (!this.$register && !this.externalPaymentConnectorMode) return

        // This is a watchdog timer to handle a situation where a notification is not received.
        if (this.paymentConnectorPaymentInProgress) {
          this.paymentConnectorStatusInterval = setInterval(() => {
            this.$paymentConnectorHandler.sendPaymentStatusRequest(this.currentPaymentConnectorPayment.id)
          }, 30000)
        } else if (this.paymentConnectorStatusInterval) {
          clearInterval(this.paymentConnectorStatusInterval)
        }
      },
      immediate: true
    },
    paymentConnectorTransactionOptions: {
      handler: function () {
        this.order.paymentConnectorTransactionOptions = null
        const newOptions = {}
        if (this.paymentConnectorTransactionOptions) {
          _.each(this.paymentConnectorTransactionOptions, option => {
            newOptions[option.key] = option.default
          })
        }

        this.order.paymentConnectorTransactionOptions = newOptions
      },
      immediate: true
    },
    promptForManualCard () {
      if (this.promptForManualCard) {
        this.$payments.cancelExistingTerminalCollectionAttempt()
      } else {
        this.configureStripe()
      }
    },
    '$payments.status.currentReader' () {
      if (!this.$payments.status.currentReader) return

      this.configureStripe()
    },
    '$payments.status.connected' () {
      if (!this.$payments.status.connected) return

      this.configureStripe()
    },
    'order.errors' () {
      if (!_.isEmpty(this.order.errors)) return

      this.configureStripe()
    },
    selectedPaymentType () {
      if (!this.selectedPaymentType) return

      this.addPaymentType(this.selectedPaymentType)

      this.selectedPaymentType = null
    },
    isCashAllowed () {
      this.cashSessionLoading = false
    }
  },
  created () {
    _.extend(this, {
      required,
      region,
      PAYMENT_TYPES,
      PAYMENT_STATES,
      PAYMENT_SUBTYPES
    })
  },
  mounted () {
    if (this.placeType === 'QuickCash') {
      this.addCash('exact', true)
      return
    }

    if (this.$kiosk && this.areOrderContactDetailsRequired) {
      this.$refs.orderContactDetailsDialog.show()
    } else {
      this.initPaymentsForm()
    }
  },
  beforeUnmount () {
    this.$payments.cancelExistingTerminalCollectionAttempt()
    if (this.kioskStripeTimeoutInterval !== null) {
      clearInterval(this.kioskStripeTimeoutInterval)
    }
    this.cancelPaymentConnectorTransaction()
    if (this.$paymentConnectorHandler) {
      this.$paymentConnectorHandler.cancel = null
      this.$paymentConnectorHandler.updated = null
    }

    if (this.paymentConnectorStatusInterval) {
      clearInterval(this.paymentConnectorStatusInterval)
    }

    if (this.card) {
      this.card.unmount('#manual-card-element')
      this.card.removeAllListeners()
      this.card.destroy()
    }
  },
  methods: {
    initPaymentsForm () {
      this.configurePaymentConnectorHandlers()

      const orderIncludesGiftCards = _.some(this.orderGiftCardPaymentMethods)
      const skipInitialStripeConfiguration = this.$kiosk && orderIncludesGiftCards

      // Skip the initial Stripe configuration if gift cards are being used on the
      // kiosk. In that case, the Stripe configuration will be handled with the
      // kiosk initialization.
      if (!skipInitialStripeConfiguration) {
        this.configureStripe()
      }

      if (this.$kiosk) {
        this.initializeKiosk()
      }
    },
    handlePhoneChange (phoneObject) {
      if (phoneObject?.isValid?.()) {
        this.user.phone = phoneObject.nationalNumber
        this.user.phoneCountryCode = phoneObject.country
      } else {
        this.user.phone = null
        this.user.phoneCountryCode = null
      }
    },
    otherPaymentClick () {
      this.$emit('display-other-options', true)
    },
    async removePayment (payment) {
      if (payment.state === PAYMENT_STATES.PRE_AUTH) {
        const results = await payment.cancelPreAuth()
        if (results.errors?.length) {
          this.$mh.showErrors(results.errors)
        }
        await payment.fetch(true)
        this.paymentSlideItemsKey++
      } else {
        this.order.payments.remove(payment)
      }
      this.resetSplitPayments()
    },
    otherPaymentCashButton () {
      this.$emit('display-cash-options')
    },
    async placeConsumerTabOrder (callback = false) {
      if (!this.order.hasSavedPaymentMethod && !callback) {
        this.$refs.setupIntentDialog.show(this.order, this.placeConsumerTabOrder)
      } else if (this.order.hasSavedPaymentMethod) {
        this.placeOrder(true)
      }
    },
    async selectUser (user) {
      if (user.isInvoiceable) {
        const orderUser = this.order.setRelated('user', { id: user.id })
        this.$refs.invoiceUserTable.hide()
        this.showInvoiceUserTable = false
        await orderUser.fetch(true)
      } else {
        this.$emit('edit-user', user, true)
      }
    },
    async payAtCounter () {
      this.$q.loading.show()
      await this.order.save()
      await this.order.printKioskOrder()
      this.$q.loading.hide()
      this.$emit('order-placed')
    },
    configurePaymentConnectorHandlers () {
      if (!this.$paymentConnectorHandler) return

      this.$paymentConnectorHandler.updated = this.paymentConnectorHandler

      if (this.$kiosk) {
        this.$paymentConnectorHandler.cancel = this.cancelPaymentConnectorTransaction
      }
    },
    colorForState (state) {
      if (state === 'completed') return 'bg-responsive-grey-4'
      if (state === 'canceled') return 'bg-orange-3'
      return 'white'
    },
    async payWithStripeReader () {
      this.stripeReaderPaymentRequested = true
      await this.configureStripe()
    },
    async paymentConnectorHandler (paymentId, refetchOrder) {
      this.kioskPaymentErrorMessage = null
      this.$kiosk && this.$refs.kioskPaymentErrorDialog.hide()

      if (this.order.isLocal) {
        Notify.create({
          message: 'A payment notification was received but does not match current order',
          type: 'negative',
          position: 'top'
        })
        this.placing = false
        this.$rollbar.error(`A payment notification was received with payment ID ${paymentId} but does not match the current local order`)
        return false
      }

      let payment = _.find(this.order.payments.data, payment => payment.id === paymentId)
      if (payment && this.$kiosk && payment?.state === 'canceled') {
        // Here we don't want to notify kiosk if we already know payment is canceled
        return false
      }

      if (!payment) {
        Notify.create({
          message: 'A payment notification was received but does not match any payment in the current order',
          type: 'negative',
          position: 'top'
        })
        this.placing = false
        this.$rollbar.error(`A payment notification was received with payment ID ${paymentId} but does not match any payment in the current order ID ${this.order.id}`)
        return false
      } else {
        if (refetchOrder) {
          // This is to handle surcharge items being added to the order
          this.order.resetRedemptions()
          await this.order.fetch(true)
          // We need to ensure we have the correct object in memory after refetch
          payment = _.find(this.order.payments.data, payment => payment.id === paymentId)
          this.order.applyRedemptions()
        } else {
          await payment.fetch(true)
        }
      }

      this.paymentConnectorActionSent = false

      if (payment.state === PAYMENT_STATES.COMPLETED && payment.amount < 0) {
        Notify.create({
          message: 'Refund Success',
          caption: payment.paymentConnectorResponseMessage,
          type: 'positive',
          position: 'top'
        })
        const itemsRequiringVoid = _(this.order.orderItems.data).filter(item => _.some(payment.orderItemIdsToVoid || [], id => id.toString() === item.id.toString())).map(orderItem => orderItem.id).value()
        if (itemsRequiringVoid.length > 0) {
          await this.order.void(itemsRequiringVoid, [])
        }
        this.placing = false
        this.$emit('order-canceled')
      } else if (payment.state === PAYMENT_STATES.COMPLETED) {
        this.saveSplitPayments(payment.amount)

        if (payment.type === PAYMENT_TYPES.CASH) {
          payment.forceShowChangeDue = true
        }

        !this.$kiosk && Notify.create({
          message: 'Payment Success',
          caption: payment.paymentConnectorResponseMessage,
          type: 'positive',
          position: 'top',
          attrs: { 'data-test-id': 'payment-connector-notification' }
        })
        this.placing = false
        const anyPaymentsInProgress = _.some(this.order.payments.data, payment => payment.paymentConnectorStatusDetails?.transaction_in_progress)
        if (this.order.remainingOwed <= 0 && !anyPaymentsInProgress) {
          await this.placeOrder()
        }
        localStorage.removeItem(PAYMENT_CONNECTOR_IN_PROGRESS_KEY)
      } else if (payment.state === PAYMENT_STATES.DRAFT && payment.paymentConnectorActionRequired) {
        Notify.create({
          message: 'A Button Selection is Required',
          type: 'Warning',
          position: 'top'
        })
      } else if (payment.paymentConnectorStatusDetails?.transaction_in_progress) {
        // Do nothing, waiting for status change
      } else if (this.$kiosk) {
        this.kioskPaymentErrorMessage = payment.paymentConnectorResponseMessage
        this.$refs.kioskPaymentErrorDialog.show()
        localStorage.removeItem(PAYMENT_CONNECTOR_IN_PROGRESS_KEY)
        this.placing = false
      } else {
        Notify.create({
          message: 'Payment Failed',
          caption: payment.paymentConnectorResponseMessage,
          type: 'negative',
          position: 'top',
          attrs: { 'data-test-id': 'payment-connector-notification' }
        })
        localStorage.removeItem(PAYMENT_CONNECTOR_IN_PROGRESS_KEY)
        this.placing = false
        if (payment.amount < 0) {
          this.$emit('order-canceled')
        }
      }
    },
    async connectToReader () {
      this.connectingToReader = true
      await this.$payments.connectToReader()
      this.connectingToReader = false
    },
    async payWithPaymentConnector () {
      if (this.$kiosk && this.paymentConnectorPaymentInProgress) {
        this.kioskPaymentErrorMessage = 'There is a transaction waiting to be cancelled, please wait a moment or pay at counter.'
        this.$refs.kioskPaymentErrorDialog.show()
        return
      }

      const requiresTransactionNumber = this.registerStation.paymentConnectorRequiresTransactionNumber
      if (requiresTransactionNumber && !this.order.paymentConnectorTransactionOptions?.transaction_number) {
        const numberResult = await this.$paymentConnectorHandler.getNextTransactionNumber()
        if (numberResult.errors && numberResult.errors.length) {
          Notify.create({
            message: 'Issue Getting Transaction Number',
            caption: _.map(numberResult.errors, 'description').join(', '),
            position: 'top',
            type: 'negative'
          })
          return
        } else if (!numberResult.data.transactionNumber) {
          Notify.create({
            message: 'No Transaction Number Available',
            caption: 'Please try again later',
            position: 'top',
            type: 'negative'
          })
          return
        } else {
          this.order.paymentConnectorTransactionOptions.transaction_number = numberResult.data.transactionNumber
        }
      }

      this.paymentConnectorRequestLoading = true
      this.placing = true
      this.paymentConnectorActionSent = false
      this.order.paymentConnectorAmount = this.paymentConnectorAmount
      this.order.action = ORDER_ACTIONS.PAYMENT_CONNECTOR_START_TRANSACTION
      const saveResult = await this.order.save()
      if (saveResult.errors && saveResult.errors.length > 0 && !this.$kiosk) {
        Notify.create({
          message: 'Issue Starting Transaction',
          caption: _.map(saveResult.errors, 'description').join(', '),
          position: 'top',
          type: 'negative'
        })
      } else if (saveResult.errors && saveResult.errors.length > 0) {
        this.kioskPaymentErrorMessage = _.map(saveResult.errors, 'description').join(', ')
        this.$refs.kioskPaymentErrorDialog.show()
      } else {
        localStorage.setItem(PAYMENT_CONNECTOR_IN_PROGRESS_KEY, this.order.id)
        localStorage.setItem(PAYMENT_CONNECTOR_PAYMENT_STARTED_AT_KEY, Date.now())
      }
      this.order.paymentConnectorTransactionOptions.transaction_number = null
      this.order.paymentConnectorAmount = 0
      this.order.action = null
      this.paymentConnectorRequestLoading = false
    },
    async forceCancelPaymentConnectorRequest () {
      await this.cancelPaymentConnectorTransaction()
      localStorage.removeItem(PAYMENT_CONNECTOR_IN_PROGRESS_KEY)
      this.$emit('order-canceled')
    },
    async performPaymentConnectorAction (action) {
      if (this.paymentConnectorActionSent) return

      this.paymentConnectorActionSent = true
      this.currentPaymentConnectorPayment.paymentConnectorActionToExecute = action
      const saveResult = await this.order.save()
      if (saveResult.errors && saveResult.errors.length > 0 && !this.$kiosk) {
        Notify.create({
          message: 'Issue Performing Action',
          caption: _.map(saveResult.errors, 'description').join(', '),
          position: 'top',
          type: 'negative'
        })
      } else if (saveResult.errors && saveResult.errors.length > 0) {
        this.kioskPaymentErrorMessage = _.map(saveResult.errors, 'description').join(', ')
        this.$refs.kioskPaymentErrorDialog.show()
      }
    },
    async cancelPaymentConnectorTransaction () {
      if (this.paymentConnectorRequestLoading) return false
      if (!this.existingPaymentConnectorPayment) return true
      this.order.action = ORDER_ACTIONS.PAYMENT_CONNECTOR_CANCEL_TRANSACTION
      const saveResult = await this.order.save()
      if (saveResult.errors && saveResult.errors.length) {
        Notify.create({
          message: 'Issue Cancelling Transaction',
          caption: _.map(saveResult.errors, 'description').join(', '),
          position: 'top',
          type: 'negative'
        })
        return false
      }
      return true
    },
    addGiftCardPayments () {
      const sortedGiftCards = _.orderBy(this.user.validGiftCardsForCurrentCircuit, ['expiresAt', 'balance'], ['asc', 'asc'])
      _.each(sortedGiftCards, giftCard => {
        if (this.order.remainingOwed) {
          this.addGiftCardPayment(giftCard)
        }
      })
    },
    giftCardAmount (giftCard) {
      const existingGiftCardPayment = this.existingGiftCardPayment(giftCard)

      return this.paymentAmount(existingGiftCardPayment?.amount, giftCard.balance)
    },
    autoPlaceGiftCard (giftCard) {
      const existingGiftCardPayment = this.existingGiftCardPayment(giftCard)
      return this.autoPlace(existingGiftCardPayment?.amount, giftCard.balance)
    },
    giftCardExhausted (giftCard) {
      const existingGiftCardPayment = this.existingGiftCardPayment(giftCard)
      return this.paymentExhausted(existingGiftCardPayment?.amount, giftCard.balance)
    },
    kioskHiddenDialogClick () {
      ++this.kioskHiddenDialogClickCount
      if (this.kioskHiddenDialogClickCount >= 10) {
        this.kioskHiddenDialogClickCount = 0
        this.$q.dialog({
          title: 'Payment Errors',
          message: this.$payments.status.error,
          persistent: true
        })
      }
    },
    // Initializes payment for the kiosk
    initializeKiosk () {
      if (this.order.totalOwed <= 0) {
        this.placeOrder()
        return
      }

      // If gift cards were added during the order, we need to add them as payments
      if (_.some(this.orderGiftCardPaymentMethods)) {
        _.each(this.orderGiftCardPaymentMethods, giftCard => this.addGiftCardPayment(giftCard))
        return
      }

      if (this.externalPaymentConnectorMode) {
        this.payWithPaymentConnector()
      }
    },
    // Determines if the provided gift card is already being used as a draft payment for the order
    existingGiftCardPayment (giftCard) {
      return _.find(this.order.payments.data, payment => payment.giftCard?.id === giftCard.id && payment.state === PAYMENT_STATES.DRAFT)
    },

    autoPlace (existingAmount, maxAmount = null) {
      existingAmount ||= 0
      return !this.split && this.paymentAmount(existingAmount, maxAmount) - existingAmount >= this.order.remainingOwed
    },
    paymentExhausted (existingAmount, maxAmount) {
      return (existingAmount || 0) >= (maxAmount || 0)
    },
    addCash (amount, placeOrder = false) {
      if (!this.isCashAllowed) {
        this.showCashSessionDialog = true
      } else {
        if (!this.split) {
          let existingCashPayment = _.find(this.order.payments.data, { type: PAYMENT_TYPES.CASH, state: PAYMENT_STATES.DRAFT })

          if (!existingCashPayment) {
            existingCashPayment = this.order.payments.add({
              type: PAYMENT_TYPES.CASH,
              amount: 0
            })
          }

          if (amount === 'exact') {
            existingCashPayment.amount = this.order.remainingOwed + existingCashPayment.amount
            existingCashPayment.cashReceived = existingCashPayment.amount
          } else {
            existingCashPayment.cashReceived = existingCashPayment.cashReceived + amount
            existingCashPayment.amount = existingCashPayment.amount + _.min([this.order.remainingOwed, amount])
          }
        } else {
          const cashPayment = this.order.payments.add({
            type: PAYMENT_TYPES.CASH,
            amount: 0
          })

          if (amount === 'exact') {
            cashPayment.amount = this.order.remainingOwed
            cashPayment.cashReceived = cashPayment.amount
          } else {
            cashPayment.cashReceived = amount
            cashPayment.amount = _.min([this.order.remainingOwed, this.splitAmount, amount])
          }
        }

        if (this.paymentConnectorRequiredForCashPayments && placeOrder) {
          this.placeOrder()
          // We want to avoid saving split payments, since this is done if the payment is successful
          return
        } else if (this.paymentConnectorRequiredForCashPayments) {
          // We want to avoid saving split payments, since this is done if the payment is successful
          return
        }

        this.saveSplitPayments(_.min([this.order.remainingOwed, this.splitAmount, amount]))

        if (placeOrder) {
          this.placeOrder()
        }
      }
    },
    setCash (amount) {
      let existingCashPayment = _.find(this.order.payments.data, { type: PAYMENT_TYPES.CASH, state: PAYMENT_STATES.DRAFT })

      if (!existingCashPayment) {
        existingCashPayment = this.order.payments.add({
          type: PAYMENT_TYPES.CASH,
          amount: 0
        })
      }

      existingCashPayment.amount = 0
      existingCashPayment.cashReceived = amount
      existingCashPayment.amount = _.min([this.order.remainingOwed, amount])
    },
    addAccountCredit () {
      const autoPlace = this.autoPlaceCredit
      const amount = this.creditAmount
      if (!this.existingCreditPayment) {
        this.order.payments.add({
          type: 'credit',
          amount
        })
        this.saveSplitPayments(amount)
      } else if (this.split) {
        this.existingCreditPayment.amount += amount
        this.saveSplitPayments(amount)
      } else {
        this.existingCreditPayment.amount = amount
      }

      if (autoPlace && this.$register) {
        this.placeOrder()
      }
    },
    addPaymentType (paymentType) {
      if (paymentType.requiresAuthorization && !this.currentUser.admin) {
        this.$emit('request-authorization', () => { this.addPaymentTypeAuthorized(paymentType) })
      } else {
        this.addPaymentTypeAuthorized(paymentType)
      }
    },
    addPaymentTypeAuthorized (paymentType) {
      const dialogOptions = {
        title: 'Pay by ' + paymentType.name,
        message: paymentType.promptMessage,
        cancel: {
          color: 'negative'
        },
        ok: {
          label: 'Confirm Payment',
          color: 'positive'
        }
      }

      if (paymentType.hasDetailText) {
        dialogOptions.prompt = {
          model: '',
          label: `${paymentType.detailTextFieldName} ` + `(${paymentType.detailTextRequired ? this.$i18n.t('common.required') : this.$i18n.t('common.optional')})`,
          isValid: val => !paymentType.detailTextRequired || (paymentType.detailTextRequired && val.length > 2),
          type: 'text'
        }
      }

      if (paymentType.hasPrompt) {
        Dialog.create(dialogOptions).onOk((reasonText) => {
          this.addPaymentTypeConfirmed(paymentType, reasonText)
        })
      } else {
        this.addPaymentTypeConfirmed(paymentType)
      }
    },
    addPaymentTypeConfirmed (paymentType, reasonText = null) {
      const autoPlace = !this.split
      const amount = this.existingPaymentTypePaymentAmounts[paymentType.id]

      if (!this.existingPaymentTypePayments[paymentType.id]) {
        this.order.payments.add({
          paymentTypeId: paymentType.id,
          label: paymentType.name,
          paymentTypeReasonText: reasonText,
          amount
        })
        this.saveSplitPayments(amount)
      } else if (this.split) {
        this.existingPaymentTypePayments[paymentType.id].amount += amount
        this.saveSplitPayments(amount)
      } else {
        this.existingPaymentTypePayments[paymentType.id].amount = amount
      }

      if (autoPlace) {
        this.placeOrder()
      }
    },
    getTextColor (backgroundColor) {
      if (this.split) return backgroundColor

      if (this.$isColorDark(backgroundColor)) {
        return 'white'
      } else {
        return 'black'
      }
    },
    computeSplitAmount (splitRatio) {
      return Math.round(this.order.total / splitRatio * 100) / 100
    },
    // This method determines how much of the order total is remaining to pay
    // after the given amount has been paid
    saveSplitPayments (amount = this.splitAmount) {
      this.customSplitAmount = 0

      _.each(this.order.orderItems.data, orderItem => {
        if (this.order.remainingOwed === 0) {
          orderItem.paidAmount = orderItem.total
          orderItem.splitRatio = 0
          orderItem.payAmount = 0
          this.customSplitAmount = 0
        } else {
          const paidAmount = _.min([amount, orderItem.payAmount])

          orderItem.paidAmount += paidAmount
          amount -= paidAmount

          if (orderItem.paidAmount === orderItem.total) {
            orderItem.splitRatio = 0
            orderItem.payAmount = 0
          } else {
            orderItem.payAmount = _.min([orderItem.payAmount, orderItem.total - orderItem.paidAmount])
          }

          this.customSplitAmount += orderItem.payAmount
        }
      })

      if (this.customSplitAmount === 0) {
        this.customSplitAmount = this.order.remainingOwed
      }
    },
    resetSplitPayments () {
      _.each(this.order.orderItems.data, orderItem => {
        orderItem.splitRatio = 0
        orderItem.paidAmount = 0
      })
    },
    payWithPreAuthPaymentMethod () {
      const payment = this.order.preAuthPayment
      if (!payment) return

      if (this.preAuthPaymentMethodAmount < payment.amount) {
        this.$mh.showErrors([{ description: 'The payment amount must be greater than the pre-auth amount' }])
        return
      } else {
        payment.amount = this.preAuthPaymentMethodAmount
        this.saveSplitPayments(this.preAuthPaymentMethodAmount)
      }

      if (!this.split) {
        this.placeOrder()
      }
    },

    payLater () {
      if (this.order.hasDraftPayments) {
        this.$refs.payLaterDialog.show()
      } else {
        this.placeOrder(true)
      }
    },

    payWithDefaultPaymentMethod () {
      if (!this.existingDefaultPaymentMethod) {
        // Default payment method doesn't exist, add with automatic amount
        const initialData = {
          type: PAYMENT_TYPES.CARD,
          subtype: PAYMENT_SUBTYPES.SAVED,
          stripePaymentMethodId: this.user.defaultPaymentMethodId,
          stripePaymentMethodFingerprint: this.user.defaultPaymentMethodFingerprint,
          lastFour: this.user.defaultPaymentMethodLast4,
          cardType: this.user.defaultPaymentMethodBrand,
          amount: this.defaultPaymentMethodAmount
        }
        this.order.payments.add(initialData)
        this.saveSplitPayments(this.defaultPaymentMethodAmount)
      } else {
        this.existingDefaultPaymentMethod.amount = this.defaultPaymentMethodAmount
        this.saveSplitPayments(this.defaultPaymentMethodAmount)
      }

      if (!this.split) {
        this.placeOrder()
      }
    },

    payWithSavedPaymentMethod () {
      if (!this.existingDefaultPaymentMethod) {
        // Default payment method doesn't exist, add with automatic amount
        const initialData = {
          type: PAYMENT_TYPES.CARD,
          subtype: PAYMENT_SUBTYPES.SAVED_TAB,
          stripePaymentMethodId: this.order.savedPaymentMethodId,
          stripePaymentMethodFingerprint: this.order.savedPaymentMethodFingerprint,
          lastFour: this.order.savedPaymentMethodLast4,
          cardType: this.order.savedPaymentMethodBrand,
          amount: this.savedPaymentMethodAmount
        }
        this.order.payments.add(initialData)
        this.saveSplitPayments(this.savedPaymentMethodAmount)
      } else {
        this.existingSavedPaymentMethod.amount = this.savedPaymentMethodAmount
        this.saveSplitPayments(this.savedPaymentMethodAmount)
      }

      if (!this.split) {
        this.placeOrder()
      }
    },
    // Adds a 'gift card' payment to the order given a Gift Card object, and determines
    // the remaining amount to be paid after the gift card is applied
    addGiftCardPayment (giftCard) {
      const autoPlace = this.autoPlaceGiftCard(giftCard)
      const amount = this.giftCardAmount(giftCard)

      const existingGiftCardPayment = this.existingGiftCardPayment(giftCard)

      if (!existingGiftCardPayment) {
        this.order.payments.add({
          type: 'gift-card',
          giftCard,
          amount
        })
        this.saveSplitPayments(amount)
      } else if (this.split) {
        existingGiftCardPayment.amount += amount
        this.saveSplitPayments(amount)
      } else {
        existingGiftCardPayment.amount = amount
      }

      if (autoPlace) {
        this.placeOrder()
      }
    },
    validate () {
      if (this.areOrderContactDetailsRequired && !this.areOrderContactDetailsValidated) {
        const validated = this.$refs.orderContactDetails?.validate()
        if (!validated) return false
      }

      if (this.order.user && this.$refs.userPhone) {
        const phoneValid = this.$refs.userPhone.validate()

        if (phoneValid && this.user.phone && this.user.isDirty) {
          this.user.save()
        }

        if (!phoneValid) return false
      }

      return true
    },

    async saveAndGetPaymentIntent (terminal = false, preauth = false) {
      if (this.creditCardAmount <= 0) return

      this.$emit('loading-payment-intent', true)
      this.order.preventOrderItemValidationsDialog = true

      // Create the payment intent
      const stripePaymentIntentClientSecret = await this.order.createPaymentIntent(this.creditCardAmount, terminal, preauth, errors => {
        if (errors && errors.length && _.startsWith(errors[0].description, 'Payment does not match total')) {
          let messages = [
            this.$i18n.t('pages.payment.errors.priceForItemsHaveChanged'),
            this.$i18n.t('pages.payment.confirmNewPriceAndRetryPrompt')
          ]
          const validationsToDisplay = _.difference(this.order.draftOrderItemValidationMessages, this.order.previousDraftOrderItemValidationMessages)
          this.order.previousDraftOrderItemValidationMessages = this.order.draftOrderItemValidationMessages
          if (validationsToDisplay.length) {
            messages = validationsToDisplay
          }
          this.$q.dialog({
            title: this.$i18n.t('pages.payment.priceChangeHeading'),
            message: messages.join('<br><br>'),
            html: true
          })
          this.order.configuration.validate()
          return
        }

        this.$mh.showErrors(errors)
      })
      this.$emit('loading-payment-intent', false)

      if (!stripePaymentIntentClientSecret) {
        return null
      }

      return stripePaymentIntentClientSecret
    },

    async payWithNewManualCard (recapchaValid = false) {
      if (this.placing) return

      this.placing = true
      if (this.disablePayWithNewManualCard) {
        this.placing = false
        return
      }

      if (!this.validate()) {
        this.placing = false
        return
      }

      this.loading = true
      const clientSecret = await this.saveAndGetPaymentIntent()
      const orderAwaitingPayment = { id: this.order.id }

      if (!clientSecret) {
        this.loading = false
        this.placing = false
        return
      }

      this.stripe
        .confirmCardPayment(clientSecret, {
          payment_method: {
            card: this.card
          }
        })
        .then((result) => {
          if (result.error) {
            this.showError(result.error.message)
            this.loading = false
            this.placing = false
          } else {
            this.placing = false
            this.paymentSucceeded(result.paymentIntent, 'manual', orderAwaitingPayment)
            this.loading = false
          }
        })
    },

    // This is the Apple / Google Pay Button
    async setupOnePaymentButton () {
      if (!this.paymentRequest) {
        this.paymentRequest = this.stripe.paymentRequest({
          country: this.currentCircuit.iso31661,
          currency: this.currentCircuit.currency.toLowerCase(),
          total: {
            label: 'Total',
            amount: Math.round(this.creditCardAmount * 100)
          },
          requestPayerName: true,
          requestPayerEmail: true
        })
      }

      const elements = this.stripe.elements()
      const paymentRequestButton = elements.create('paymentRequestButton', {
        paymentRequest: this.paymentRequest,
        style: {
          paymentRequestButton: {
            height: '52px'
          }
        }
      })

      // Check the availability of the One Button Payment Request API first.
      const result = await this.paymentRequest.canMakePayment()

      const mountOneButtonPay = () => {
        if (!this.$isFeatureEnabled(this.$featureFlags.disable_google_recaptcha)) {
          // We only want to mount the one button pay once recaptcha is successful
          this.recaptchaCallback = () => {
            this.oneButtonPayAvailable = true
            paymentRequestButton.mount('#one-button-pay')
            paymentRequestButton.on('click', (event) => {
              if (!this.validate()) {
                event.preventDefault()
              }
            })
          }
          this.$nextTick(() => {
            this.$refs.recaptcha.executeCaptcha()
          })
        } else {
          // We are not using recaptcha so we can skip the recaptcha step
          this.oneButtonPayAvailable = true
          paymentRequestButton.mount('#one-button-pay')
          paymentRequestButton.on('click', (event) => {
            if (!this.validate()) {
              event.preventDefault()
            }
          })
        }
      }

      if (result) {
        mountOneButtonPay()
      } else {
        this.oneButtonPayAvailable = false
        const oneButtonPayElement = document.getElementById('one-button-pay')
        if (oneButtonPayElement) {
          oneButtonPayElement.style.display = 'none'
        }
      }

      // Listen for user chosing to pay
      this.paymentRequest.on('paymentmethod', (event) => {
        this.processStripeOneButton(event)
      })
    },

    updateOnePaymentButton () {
      if (!this.creditCardAmount) {
        this.oneButtonPayAvailable = false
        return
      }

      this.paymentRequest.update({
        total: {
          label: 'Total',
          amount: Math.round(this.creditCardAmount * 100)
        }
      })
    },

    async processStripeOneButton (event) {
      if (this.placing) return

      this.placing = true
      if (!this.validate()) {
        event.complete('fail')
        this.placing = false
        return
      }

      const clientSecret = await this.saveAndGetPaymentIntent()
      const orderAwaitingPayment = { id: this.order.id }

      if (!clientSecret) {
        event.complete('fail')
        this.placing = false
        return
      }

      // Confirm the PaymentIntent without handling potential next actions (yet).
      this.stripe.confirmCardPayment(
        clientSecret,
        { payment_method: event.paymentMethod.id },
        { handleActions: false }
      ).then((confirmResult) => {
        if (confirmResult.error) {
          // Report to the browser that the payment failed, prompting it to
          // re-show the payment interface, or show an error message and close
          // the payment interface.
          event.complete('fail')
          this.placing = false
        } else {
          // Report to the browser that the confirmation was successful, prompting
          // it to close the browser payment method collection interface.
          event.complete('success')
          // Check if the PaymentIntent requires any actions and if so let Stripe.js
          // handle the flow.
          if (confirmResult.paymentIntent.status === 'requires_action') {
            // Let Stripe.js handle the rest of the payment flow.
            this.stripe.confirmCardPayment(clientSecret).then((result) => {
              this.placing = false // Needs to be before payment succeeded so that payment succeeded can place the order

              if (result.error) {
                // Payment failed, ask for new method
                this.showError(result.error.message)
              } else {
                this.paymentSucceeded(confirmResult.paymentIntent, 'wallet', orderAwaitingPayment)
              }
            })
          } else {
            this.placing = false // Needs to be before payment succeeded so that payment succeeded can place the order
            this.paymentSucceeded(confirmResult.paymentIntent, 'wallet', orderAwaitingPayment)
          }
        }
      })
    },

    // Setup the UI to allow the user to add a new card
    setupAddNewCard (stripe) {
      if (this.$kiosk || (this.card && this.promptForManualCard && this.order.phoneOrder) || this.$mobileRegister()) return

      if (this.card) {
        this.card.unmount('#manual-card-element')
        this.card.removeAllListeners()
        this.card.destroy()
      }

      const elements = stripe.elements()

      this.card = elements.create('card')

      this.card.mount('#manual-card-element')

      this.card.on('change', (event) => {
        this.stripeCardElementOnChange = true
        this.disablePayWithNewManualCard = !!event.empty || !!event.error || !event.complete
        this.payWithNewManualCardErrors = event.error ? event.error.message : ''
      })
      this.card.on('focus', (event) => {
        this.stripeCardElementOnEditing = true
      })
      this.card.on('blur', (event) => {
        this.stripeCardElementOnEditing = false
      })
    },

    // Setups up the Stripe SDK and Stripe components in preparation to accept payment
    async configureStripe () {
      const isNewPhoneOrder = this.order.phoneOrder && this.order.draft

      if (this.order.remainingOwed <= 0) return

      if (this.order.action) return

      if (this.order.errors) return

      if (this.creditCardAmount <= 0) return

      // Initialize the Stripe SDK
      this.stripe = await this.$payments.configureStripe(this.currentSite.standardConnectAccountId)

      this.setupAddNewCard(this.stripe)

      const shouldUseStripeReaderAutoPay = (this.currentSite.stripeReaderAutoPay && !this.$mobileRegister()) || this.$kiosk || this.stripeReaderPaymentRequested

      // Prompt for payment via Stripe terminal if a reader is connected
      if (!isNewPhoneOrder && !this.externalPaymentConnectorMode && shouldUseStripeReaderAutoPay) {
        await this.configureStripeTerminal()
      }
    },

    editPayment (payment) {
      if (payment.type === PAYMENT_TYPES.CASH) {
        this.editCashPayment()
      }
    },

    editCashPayment () {
      if (this.existingCashPayment) {
        this.$refs.editCashDialog.show(this.existingCashPayment.cashReceived)
      } else {
        this.cashReceived = 0
        this.$refs.editCashDialog.show(this.cashReceived)
      }
    },

    // Prompt for payment via Stripe terminal
    async configureStripeTerminal () {
      if (!this.collectWithChipReader) {
        this.stripeReaderPaymentRequested = false
        return
      }

      // If we were already attempting to charge and this is a change, cancel the attempt first thing
      await this.$payments.cancelExistingTerminalCollectionAttempt()

      this.loadingTerminalIntent = true

      this.terminalIntentToken = null

      await this.$payments.configureStripeTerminal()
      this.loadingTerminalIntent = false

      if (!this.$payments.status.connected) {
        this.stripeReaderPaymentRequested = false
        return
      }

      // Create a payment intent for the terminal
      this.terminalIntentToken = await this.saveAndGetPaymentIntent(true)
      if (!this.terminalIntentToken) {
        this.stripeReaderPaymentRequested = false
        return
      }

      const orderAwaitingPayment = { id: this.order.id }

      const skipTipping = !this.showTipping

      this.kioskStripeTimeoutInterval = setInterval(() => {
        this.$emit('timeout')
      }, 60000)

      const { collectResult, error } = await this.$payments.collectTerminalPaymentMethod(this.terminalIntentToken, skipTipping, this.$mobileRegister())
      if (error) {
        this.stripeReaderPaymentRequested = false
        return
      }

      clearInterval(this.kioskStripeTimeoutInterval)
      this.kioskStripeTimeoutInterval = null

      const paymentResult = await this.$payments.processTerminalPayment(collectResult)

      if (paymentResult.success) {
        this.paymentSucceeded(collectResult.paymentIntent, 'present', orderAwaitingPayment)
      } else if (this.$kiosk) {
        this.kioskPaymentErrorMessage = paymentResult.error
        this.$refs.kioskPaymentErrorDialog && this.$refs.kioskPaymentErrorDialog.show()
      }

      this.stripeReaderPaymentRequested = false
    },

    showError (error) {
      this.$mh.showErrors([error])
    },

    async addGiftCard () {
      if (!this.giftCardCode.length) return

      this.giftCardLoading = true

      const giftCard = new GiftCard({
        options: {
          queryName: 'giftCardByUid',
          initialValue: 'default',
          balance: 'default',
          variables: {
            uid: this.giftCardCode
          }
        }
      })
      await giftCard.fetch(true)

      this.giftCardLoading = false

      if (!giftCard.isRemote) {
        this.giftCardErrorMessage = this.$i18n.t('giftCard.errors.notFound')
        return
      }

      if (giftCard.balance === 0) {
        this.giftCardErrorMessage = this.$i18n.t('giftCard.balanceAmount', { amount: this.$i18n.n(0, 'currency') })
        return
      }

      if (giftCard.state === 'expired') {
        this.giftCardErrorMessage = this.$i18n.t('giftCard.errors.expired')
        return
      }

      if (giftCard.state !== 'valid') {
        this.giftCardErrorMessage = this.$i18n.t('giftCard.errors.notValid')
        return
      }

      this.order.addGiftCard(giftCard)
      this.promptForGiftCard = false
      this.giftCardErrorMessage = null
      this.giftCardCode = ''
    },

    async validateCeaCard () {
      if (!this.ceaCardVoucherType) return
      // Validate card number
      const ceaCard = new CeaCard({ cardNumber: this.promoCode })
      await ceaCard.save()
      ceaCard.destroy()
      if (!ceaCard.valid) {
        return
      }

      const isValidCeaOder = this.order.isValidCeaOrder
      if (this.order.ceaCardApplied) {
        this.promoCodeErrorMessage = 'CEA card has already been applied.'
        this.promoCodeLoading = false
      } else if (!isValidCeaOder) {
        this.promoCodeErrorMessage = 'Card valid, order does not contain any redeemable CEA tickets.'
        this.promoCodeLoading = false
      } else {
        this.order.addIndependentVoucher(new Voucher({ voucherTypeId: this.ceaCardVoucherType.id }))
        this.promoCodeLoading = false
        this.promoCodeErrorMessage = ''
        this.promoCode = ''
      }
      return true
    },

    async addPromoCode () {
      this.promoCodeLoading = true
      const resolvedDiscount = await this.discounts.matchingOnlineDiscountCode(this.promoCode)
      const ceaCardRegex = /^\d{9}$/
      if (!resolvedDiscount && this.ceaCardVoucherType && ceaCardRegex.test(this.promoCode) && await this.validateCeaCard()) {
        return
      }

      if (!resolvedDiscount) {
        this.promoCodeErrorMessage = this.ceaCardVoucherType ? this.$i18n.t('pages.payment.errors.invalidPromoCodeOrCEACard') : this.$i18n.t('pages.payment.errors.invalidPromoCode')
        this.promoCodeErrorRestrictions = ''
      } else if (resolvedDiscount.limitRedemptions && resolvedDiscount.redemptionCount >= resolvedDiscount.redemptionsLimit) {
        this.promoCodeErrorMessage = this.$i18n.t('pages.payment.errors.limitedRunDiscountNoLongerValid')
        this.promoCodeErrorRestrictions = ''
      } else {
        this.order.addIndependentDiscount(resolvedDiscount)
        if (_.some(this.order.appliedDiscounts, (applied) => applied.id === resolvedDiscount.id)) {
          // promo code applied
          this.promoCodeErrorMessage = ''
          this.promoCode = ''
        } else {
          // Remove discount
          this.order.removeIndependentDiscount(resolvedDiscount)

          const promoCodeDescription = resolvedDiscount.details
          if (promoCodeDescription.restrictions.length) {
            this.promoCodeErrorMessage = promoCodeDescription.message
            this.promoCodeErrorRestrictions = promoCodeDescription.restrictions
          }
        }
      }

      this.promoCodeLoading = false
    },

    async paymentSucceeded (paymentIntent, subtype, order = this.order) {
      // The order associated with this payment is not the current order, try to recover
      if (order.id !== this.order.id) {
        this.$rollbar.error(`Order (${order.id}) payment (${paymentIntent.id}) succeeded late. The order is not in the current context (${this.order.id})`)
        order = new Order({ id: order.id }, ORDER_CHECKOUT_DEFAULT_OPTIONS)
        await order.fetch(true)
      } else {
        order = this.order
      }

      // If the order.id is < 0 we were not able to find the intended order (order id would have been set to get the payment intent)
      // If the order.id is the current order, but the client secrets are not matching, something has gone wrong before accepting the payment
      // We can't check the stripe intent for orders that are not this order, as it is only returned on intent creation
      if ((order.id < 0) ||
          (order.id === this.order.id && order.stripePaymentIntentClientSecret !== paymentIntent.client_secret)) {
        // Notify the user and refund the payment
        const errorMessage = `Payment (${paymentIntent.id}) of ${this.$n(paymentIntent.amount / 100.0, 'currency')} is not associated with currently selected Order (${this.order.id}). The Order changed before the Payment was processed. The Payment is being refunded automatically.`
        this.$mh.showErrors([errorMessage])
        this.$rollbar.error('Payment not associated with current order. The Order changed before the Payment was processed.', { message: errorMessage })

        const refundResult = await this.$payments.refundPayment(paymentIntent)
        if (refundResult.errors.length) {
          this.$mh.showErrors([`Unable to automatically refund payment (${paymentIntent.id}) for ${this.$n(paymentIntent.amount / 100.0, 'currency')}`, refundResult.errors])
          this.$rollbar.critical(`Unable to refund detached payment (${paymentIntent.id}) for order (${order.id})`)
        } else {
          this.$mh.notify(`Successfully refunded Payment for ${this.$n(paymentIntent.amount / 100.0, 'currency')}`)
        }
        return
      }

      // Only add the payment to the order if it does not already exist
      const payment = {
        type: PAYMENT_TYPES.CARD,
        subtype,
        stripePaymentIntentId: paymentIntent.id,
        amount: paymentIntent.amount / 100.00
      }
      if (!_.find(order.payments?.data, { stripePaymentIntentId: payment.stripePaymentIntentId })) {
        order.payments.add(payment)
        this.saveSplitPayments(paymentIntent.amount)
      }

      if (!this.split) {
        this.$kiosk && this.$q.loading.show()
        await this.placeOrder(false, order)
        this.$kiosk && this.$q.loading.hide()
      } else {
        await order.save()
        this.configureStripe()
      }
    },

    async placeOrder (tab = false, order = this.order, processTabPayments = false) {
      if (this.placing) return

      const cashPaymentInDraft = _.find(order.payments.data, { type: PAYMENT_TYPES.CASH, state: PAYMENT_STATES.DRAFT })
      if (this.paymentConnectorRequiredForCashPayments && cashPaymentInDraft) {
        return this.payWithPaymentConnector()
      }

      this.placing = true

      if (!this.validate()) {
        this.placing = false
        return
      }

      if (!tab && order.remainingOwed > 0) {
        this.$mh.showErrors([this.$i18n.t('payment.required')])
        this.placing = false
        return
      }

      const emptyDonationItem = _.find(order.orderItems.data, orderItem => orderItem.type === ORDER_ITEM_TYPES.DONATION && orderItem.isDraft && orderItem.amount === 0)
      if (emptyDonationItem) {
        order.orderItems.remove(emptyDonationItem)
      }

      // This is also called in the place method in order.js, we need to call before place() to calculate the change due.
      order.addCashOutItems()

      const purchasingMembership = order.purchasingMembership
      const total = order.total
      const changeDue = order.changeDue
      order.resetChangeDuePayments()

      this.loading = true

      if (this.receiveMarketingEmails) {
        const anonymousUser = new User({
          firstName: this.order.deliveryFirstName,
          lastName: this.order.deliveryLastName,
          email: this.order.deliveryEmailAddress,
          receiveMarketingEmails: true
        })

        anonymousUser.createUserMarketingSubscriptions().then(() => {
          this.$trackEvent(this.$customerEvents.SUBSCRIBED_TO_MARKETING_LISTS)
        })
      }

      let placeType

      if (tab && processTabPayments) {
        placeType = ORDER_PLACE_TYPES.TAB_ORDER_AND_PAYMENTS
      } else if (tab) {
        placeType = ORDER_PLACE_TYPES.TAB_ORDER
      } else {
        placeType = ORDER_PLACE_TYPES.FULL_ORDER
      }

      const success = await order.place(placeType, errors => {
        if (this.$kiosk) {
          this.payAtCounter()
          Dialog.create({
            title: this.$i18n.t('pages.payment.errors.issueWithOrder'),
            message: this.$i18n.t('pages.payment.pleaseTakeYourReceiptToCounter')
          })
        } else {
          this.$mh.showErrors(errors)
        }
      })
      this.loading = false

      if (success && this.order.id === order.id) {
        const eventParams = { membershipOrder: purchasingMembership, currency: this.currentCircuit.currency, total, changeDue }
        this.$emit('order-placed', eventParams)
        localStorage.removeItem(PAYMENT_CONNECTOR_IN_PROGRESS_KEY)
      } else if (success) {
        this.$mh.notify(`Payment for Order (${order.id}) was delayed, but processed successfully.\n Check the Order if this Payment should not have been processed.`)
      }

      this.placing = false
    },

    async cancelOrder () {
      if (this.order.state !== 'pending') {
        this.$mh.showErrors([this.$i18n.t('pages.payment.errors.canOnlyCancelPendingOrder')])
        return
      }

      const purchasingMembership = this.order.purchasingMembership

      this.loading = true
      const success = await this.order.cancel(errors => {
        this.loading = false
        this.$mh.showErrors(errors)
      })
      this.loading = false

      if (success) {
        this.$emit('order-canceled', purchasingMembership)
      }
    },
    async findInvoiceUser () {
      this.showInvoiceUserTable = true
      await this.$nextTick()
      this.$refs.invoiceUserTable.show()
    },
    openCashSession () {
      this.cashSessionLoading = true
      this.$emit('open-cash-session')
    },
    validateOrderContactDetails () {
      if (this.$refs.orderContactDetails?.validate()) {
        this.areOrderContactDetailsValidated = true
        this.initPaymentsForm()
        this.$refs.orderContactDetailsDialog.hide()
      }
    }
  },
  configuration: ['currentCircuit', 'currentSite', 'discounts', 'paymentTypes', 'voucherTypes'],
  context: ['currentRegisterClient', 'currentUser']
}

</script>

<style lang="scss" scoped>
h2 {
  color: var(--q-primary);
  font-size: 1.1rem;
  font-weight: bold;
  line-height: 1em;
}

.square-top {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.quick-cash-button {
  height: 44px;
}

</style>

<style lang="scss">
.bottom-square .q-field__control, .bottom-square.q-list {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
</style>
